﻿// ============================================================================
// MÓDULO 4: CLIENT MANAGER (VERSÃO 24.0 - PATH SAFETY FIX)
// ARQUIVO: src/features/clients/components/ClientManager.jsx
// OBJETIVO: Corrigir erro 'indexOf' (doc path) e integrar automação completa.
// ============================================================================

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Info, Copy } from "lucide-react";
import { httpsCallable } from "firebase/functions";

import { db, functions } from "../../../shared/api/firebase/config";
import * as repo from "./ClientManagerRepository";
import {
  CLIENT_TYPES,
  ClientSchema,
  generateQualificationText,
  ANCHOR_ROLES,
} from "../../../config/ClientSchema";
import {
  CLIENT_ORIGIN,
  CLIENT_TAGS,
  CLIENT_TAG_COLORS,
} from "../../../config/ClientConstants";
import {
  BRAZIL_STATES,
  GENDER_IDENTITIES,
  GRAMMATICAL_GENDERS,
  NATIONALITIES,
  CIVIL_STATUS,
  ORG_EMISSORES,
  PROFESSIONAL_COUNCILS,
  PROFESSIONS,
  HEALTH_CONDITIONS,
  HEALTH_LABELS,
  PRONOUNS,
  MARITAL_REGIMES_LIST,
  ANIMAL_SPECIES,
  ANIMAL_REPRODUCTIVE_STATUS,
  ANIMAL_COAT_COLORS,
  ANIMAL_COAT_PATTERNS,
  ANIMAL_COAT_TEXTURES,
  ANIMAL_BREEDS,
  ANIMAL_ROLES,
  REPRESENTATION_TYPE,
  PRISON_RELATION,
  PJ_ROLES,
  INCAPACITY_REASONS,
  CIN_ORGAO_EXPEDIDOR_POR_UF,
} from "../../../config/ClientConstants";
import { SearchEngine } from "../../../utils/SearchEngine";
import { FIREBASE_CONFIG } from "../../../config/Config";
import { InputMasked } from "../../../components/ui/InputMasked";
import { CrmClientPickerModal } from "../../../components/CrmClientPickerModal";
import { SaveCancelBar } from "../../../components/ui/SaveCancelBar";
import { LegalAPIs } from "../../../services/LegalAPIs";
import {
  getConsentActionDescriptor,
  requestDocumentConsent,
} from "../../../utils/documentConsent";
import { ClientNotesTab } from "./ClientNotesTab";
import ClientSidePeek from "./ClientSidePeek";
import BillingGenerator from "../../finance/components/BillingGenerator";
import FinancialHealthWidget from "../../finance/components/FinancialHealthWidget";
import ImportPreviewModal from "../../../components/ImportPreviewModal";
import { validateClientImport } from "../schemas/clientSchema";
import { normalizeDocumentExtractedData } from "../../../services/AIDocumentService";
import { scanForConflicts } from "../../../utils/conflictScanner";
import {
  queuePublishedDocumentEdit,
  resolvePublishedDocumentEditTarget,
} from "../../../utils/publishedDocumentEditor";

const appId = FIREBASE_CONFIG.projectId;

// ... (rest of the code remains the same)

// --- UTILITÁRIOS ---
const Masker = {
  cpf: (v) =>
    v
      .replace(/\D/g, "")
      .substring(0, 11)
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2"),
  cnpj: (v) =>
    v
      .replace(/\D/g, "")
      .substring(0, 14)
      .replace(/^(\d{2})(\d)/, "$1.$2")
      .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1/$2")
      .replace(/(\d{4})(\d)/, "$1-$2"),
  phone: (v) => {
    let r = v.replace(/\D/g, "").substring(0, 11);
    if (r.length > 10)
      return r.replace(/^(\d\d)(\d{5})(\d{4}).*/, "($1) $2-$3");
    else if (r.length > 5)
      return r.replace(/^(\d\d)(\d{4})(\d{0,4}).*/, "($1) $2-$3");
    else if (r.length > 2) return r.replace(/^(\d\d)(\d{0,5}).*/, "($1) $2");
    return r;
  },
  zip: (v) =>
    v
      .replace(/\D/g, "")
      .substring(0, 8)
      .replace(/^(\d{5})(\d)/, "$1-$2"),
  rg: (v) =>
    v
      .replace(/[^a-zA-Z0-9]/g, "")
      .substring(0, 12)
      .toUpperCase(),
  email: (v) =>
    String(v || "")
      .toLowerCase()
      .replace(/\s/g, ""),
};

const Validator = {
  email: (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email),
  cpf: (strCPF) => {
    const clean = strCPF.replace(/\D/g, "");
    if (clean.length !== 11 || /^(\d)\1+$/.test(clean)) return false;
    let soma = 0,
      resto;
    for (let i = 1; i <= 9; i++)
      soma = soma + parseInt(clean.substring(i - 1, i)) * (11 - i);
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(clean.substring(9, 10))) return false;
    soma = 0;
    for (let i = 1; i <= 10; i++)
      soma = soma + parseInt(clean.substring(i - 1, i)) * (12 - i);
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(clean.substring(10, 11))) return false;
    return true;
  },
  cnpj: (cnpj) => cnpj.replace(/\D/g, "").length === 14,
  phone: (phone) => phone.replace(/\D/g, "").length >= 10,
};

const SAFE_CLIENT_TYPES = CLIENT_TYPES || {
  PHYSICAL: "Pessoa Física",
  LEGAL: "Pessoa Jurídica",
};
const normalizeHealthCondition = (val) => {
  if (Array.isArray(val)) return val.filter((c) => c && c !== "NENHUMA");
  if (!val || val === "NENHUMA") return [];
  return [String(val)];
};
const SAFE_MASTER_DATA = {
  BRAZIL_STATES: BRAZIL_STATES || ["PR"],
  GENDER_IDENTITIES: GENDER_IDENTITIES || ["Cisgênero"],
  GRAMMATICAL_GENDERS: GRAMMATICAL_GENDERS || [
    { value: "M", label: "Masculino" },
  ],
  NATIONALITIES: NATIONALITIES || ["Brasileiro(a)"],
  CIVIL_STATUS: CIVIL_STATUS || ["Solteiro(a)"],
  MARITAL_REGIMES: MARITAL_REGIMES_LIST || [],
  ORG_EMISSORES: ORG_EMISSORES || ["SSP"],
  CIN_ORGAO_EXPEDIDOR_POR_UF: CIN_ORGAO_EXPEDIDOR_POR_UF || {},
  PROFESSIONAL_COUNCILS: PROFESSIONAL_COUNCILS || [],
  PROFESSIONS: PROFESSIONS || ["Autônomo"],
  PJ_ROLES: PJ_ROLES || [],
  ANIMAL_SPECIES: ANIMAL_SPECIES || [],
  ANIMAL_REPRODUCTIVE_STATUS: ANIMAL_REPRODUCTIVE_STATUS || [],
  ANIMAL_COAT_COLORS: ANIMAL_COAT_COLORS || [],
  ANIMAL_COAT_PATTERNS: ANIMAL_COAT_PATTERNS || [],
  ANIMAL_COAT_TEXTURES: ANIMAL_COAT_TEXTURES || [],
  ANIMAL_BREEDS: ANIMAL_BREEDS || [],
  ANIMAL_ROLES: ANIMAL_ROLES || [],
  INCAPACITY_REASONS: INCAPACITY_REASONS || [],
  REPRESENTATION_TYPE: REPRESENTATION_TYPE || [],
  PRISON_RELATION: PRISON_RELATION || [],
  HEALTH_CONDITIONS: HEALTH_CONDITIONS || ["NENHUMA"],
  PRONOUNS: PRONOUNS || ["ELE", "ELA", "ELU"],
};

const ANCHOR_ROLE_LABELS = {
  [ANCHOR_ROLES.TUTOR]: "Tutor(a)",
  [ANCHOR_ROLES.PARENT]: "Pai/Mãe",
  [ANCHOR_ROLES.PARTNER]: "Sócio/Administrador",
  [ANCHOR_ROLES.SYNDIC]: "Síndico(a)",
  [ANCHOR_ROLES.INVENTARIANTE]: "Inventariante",
  [ANCHOR_ROLES.GESTANTE_REPRESENTANTE]: "Gestante Representante",
  [ANCHOR_ROLES.ADMINISTRADOR_JUDICIAL]: "Administrador Judicial",
  [ANCHOR_ROLES.RESPONSABLE]: "Responsável",
};

const getAnchorLabelByType = (type, age) => {
  if (type === SAFE_CLIENT_TYPES.ANIMAL) return "Tutor(a) Responsável";
  if (type === SAFE_CLIENT_TYPES.LEGAL) return "Representante Legal";
  if (type === SAFE_CLIENT_TYPES.ESTATE) return "Inventariante";
  if (type === SAFE_CLIENT_TYPES.CONDOMINIUM) return "Síndico(a)";
  if (type === SAFE_CLIENT_TYPES.UNBORN || type === SAFE_CLIENT_TYPES.NASCITURO)
    return "Gestante Representante";
  if (type === SAFE_CLIENT_TYPES.BANKRUPTCY) return "Administrador Judicial";
  if (
    type === SAFE_CLIENT_TYPES.PHYSICAL &&
    typeof age === "number" &&
    age < 18
  )
    return "Quem é o Responsável (Pai/Mãe)?";
  if (type === SAFE_CLIENT_TYPES.MINOR)
    return "Quem é o Responsável (Pai/Mãe)?";
  return "Quem é o Responsável?";
};

const getDefaultAnchorRoleForType = (type) => {
  if (type === SAFE_CLIENT_TYPES.ANIMAL) return ANCHOR_ROLES.TUTOR;
  if (type === SAFE_CLIENT_TYPES.LEGAL) return ANCHOR_ROLES.PARTNER;
  if (type === SAFE_CLIENT_TYPES.ESTATE) return ANCHOR_ROLES.INVENTARIANTE;
  if (type === SAFE_CLIENT_TYPES.CONDOMINIUM) return ANCHOR_ROLES.SYNDIC;
  if (type === SAFE_CLIENT_TYPES.MINOR) return ANCHOR_ROLES.PARENT;
  if (type === SAFE_CLIENT_TYPES.UNBORN || type === SAFE_CLIENT_TYPES.NASCITURO)
    return ANCHOR_ROLES.GESTANTE_REPRESENTANTE;
  if (type === SAFE_CLIENT_TYPES.BANKRUPTCY)
    return ANCHOR_ROLES.ADMINISTRADOR_JUDICIAL;
  return ANCHOR_ROLES.TUTOR;
};

const calcAge = (birthDate) => {
  if (!birthDate) return null;
  const raw = String(birthDate).trim();
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age -= 1;
  return age;
};

const mergeClientData = (base, incoming) => {
  if (!incoming) return base;
  const merged = { ...base, ...incoming };
  merged.qualification = {
    ...base.qualification,
    ...(incoming.qualification || {}),
  };
  merged.qualification.physical = {
    ...base.qualification.physical,
    ...(incoming.qualification?.physical || {}),
  };
  merged.qualification.legal = {
    ...base.qualification.legal,
    ...(incoming.qualification?.legal || {}),
  };
  merged.qualification.minor = {
    ...base.qualification.minor,
    ...(incoming.qualification?.minor || {}),
  };
  merged.qualification.animal = {
    ...base.qualification.animal,
    ...(incoming.qualification?.animal || {}),
  };
  merged.qualification.imprisoned = {
    ...base.qualification.imprisoned,
    ...(incoming.qualification?.imprisoned || {}),
  };
  merged.qualification.unborn = {
    ...base.qualification.unborn,
    ...(incoming.qualification?.unborn || {}),
  };
  merged.qualification.estate = {
    ...base.qualification.estate,
    ...(incoming.qualification?.estate || {}),
  };
  merged.qualification.condominium = {
    ...base.qualification.condominium,
    ...(incoming.qualification?.condominium || {}),
  };
  merged.qualification.bankruptcy = {
    ...base.qualification.bankruptcy,
    ...(incoming.qualification?.bankruptcy || {}),
  };
  merged.contact = { ...base.contact, ...(incoming.contact || {}) };
  merged.location = { ...base.location, ...(incoming.location || {}) };
  merged.location.address = {
    ...base.location.address,
    ...(incoming.location?.address || {}),
  };
  return merged;
};

// --- COMPONENTES DE UI ---
const LegalLabel = ({ children, icon, required }) => (
  <label className="text-[10px] font-bold text-brand-blue/80 uppercase mb-1.5 tracking-wide flex items-center gap-1.5">
    {icon && <i className={`fas ${icon} text-highlight-action`}></i>}
    {children}
    {required && (
      <span className="text-red-500 ml-1" title="Obrigatório">
        *
      </span>
    )}
  </label>
);

const InfoTooltip = ({ text }) => (
  <span className="group relative inline-flex items-center">
    <Info className="w-3 h-3 text-gray-400 ml-1 cursor-help" />
    <span className="absolute z-50 invisible opacity-0 group-hover:visible group-hover:opacity-100 bg-gray-800 text-white text-[11px] p-2 rounded w-48 bottom-full mb-1 left-1/2 transform -translate-x-1/2 transition-all normal-case tracking-normal font-medium whitespace-normal">
      {text}
    </span>
  </span>
);

const LegalInput = ({
  className,
  error,
  mask,
  onChange,
  onBlur,
  value,
  ...props
}) => {
  const handleChange = (e) => {
    let val = e.target.value;
    if (mask) val = Masker[mask](val);
    onChange(val);
  };

  return (
    <div className="w-full">
      <input
        className={`w-full px-3 py-2.5 text-sm border rounded-lg outline-none placeholder-gray-300 bg-white disabled:bg-gray-50
                ${error ? "border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200 bg-red-50" : "border-slate-200 hover:border-slate-300 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20"} ${className}`}
        onChange={handleChange}
        onBlur={onBlur}
        value={value || ""}
        {...props}
      />
      {error && (
        <p className="text-[9px] text-red-600 font-bold mt-1 uppercase flex items-center gap-1">
          <i className="fas fa-exclamation-circle"></i> {error}
        </p>
      )}
    </div>
  );
};

const LegalSelect = ({ children, className, error, ...props }) => (
  <div className="w-full">
    <div className="relative">
      <select
        className={`w-full px-3 py-2.5 text-sm border rounded-lg outline-none bg-white appearance-none cursor-pointer
                ${error ? "border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200 bg-red-50" : "border-slate-200 hover:border-slate-300 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20"} ${className}`}
        {...props}
      >
        {children}
      </select>
      <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-slate-400">
        <i className="fas fa-chevron-down text-xs"></i>
      </div>
    </div>
    {error && (
      <p className="text-[9px] text-red-600 font-bold mt-1 uppercase flex items-center gap-1">
        <i className="fas fa-exclamation-circle"></i> {error}
      </p>
    )}
  </div>
);

const LegalHybridInput = ({
  listId,
  options,
  value,
  onChange,
  placeholder,
  ...props
}) => (
  <div className="relative group">
    <input
      list={listId}
      className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg outline-none placeholder-gray-300 bg-white hover:border-slate-300 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20"
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder || "Selecione ou digite..."}
      {...props}
    />
    <datalist id={listId}>
      {options.map((opt, idx) => (
        <option
          key={`${listId}-${idx}`}
          value={typeof opt === "object" ? opt.value : opt}
        />
      ))}
    </datalist>
    <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-slate-300 group-hover:text-brand-blue">
      <i className="fas fa-edit text-xs"></i>
    </div>
  </div>
);

const normalizeClientRoles = (roles) => {
  const allowedRoles = ["CLIENT", "PARTNER", "OTHER"];
  const source = Array.isArray(roles) ? roles : ["CLIENT"];
  const normalized = source
    .map((role) =>
      String(role || "")
        .toUpperCase()
        .trim(),
    )
    .filter((role) => allowedRoles.includes(role));
  return normalized.length > 0 ? Array.from(new Set(normalized)) : ["CLIENT"];
};

const buildRepresentativeFromPhysicalClient = (client, fallback = {}) => {
  const physical = client?.qualification?.physical || {};
  const contact = client?.contact || {};
  const address = client?.location?.address || {};
  const source = fallback || {};
  return {
    ...(source || {}),
    id: String(client?.id || source?.id || ""),
    name: String(
      physical?.name || client?.qualificationText || source?.name || "",
    ),
    nationality: String(
      physical?.nationality || source?.nationality || "brasileiro(a)",
    ),
    civilStatus: String(
      physical?.civilStatus || source?.civilStatus || "casado(a)",
    ),
    profession: String(physical?.profession || source?.profession || ""),
    rg: String(physical?.rg || source?.rg || ""),
    orgaoEmissor: String(physical?.orgaoEmissor || source?.orgaoEmissor || ""),
    cpf: String(physical?.cpf || source?.cpf || ""),
    address: {
      ...(source?.address || {}),
      street: String(address?.street || source?.address?.street || ""),
      number: String(address?.number ?? source?.address?.number ?? ""),
      complement: String(
        address?.complement ?? source?.address?.complement ?? "",
      ),
      neighborhood: String(
        address?.neighborhood || source?.address?.neighborhood || "",
      ),
      city: String(address?.city || source?.address?.city || ""),
      state: String(address?.state || source?.address?.state || ""),
      zipCode: String(address?.zipCode || source?.address?.zipCode || ""),
    },
    contact: {
      ...(source?.contact || {}),
      phonePrimary: String(
        contact?.phonePrimary ||
          contact?.phone ||
          source?.contact?.phonePrimary ||
          source?.contact?.phone ||
          "",
      ),
      phone: String(
        contact?.phonePrimary || contact?.phone || source?.contact?.phone || "",
      ),
      email: String(contact?.email || source?.contact?.email || ""),
    },
  };
};

const normalizeRepresentativeLink = (clientData, selectedClient = null) => {
  const type = clientData?.type;
  const currentAnchor = clientData?.anchor || {};
  const currentQualification = clientData?.qualification || {};

  const currentRepresentative = (() => {
    if (type === SAFE_CLIENT_TYPES.ANIMAL)
      return currentQualification?.animal?.responsible || {};
    if (type === SAFE_CLIENT_TYPES.LEGAL)
      return currentQualification?.legal?.representative || {};
    if (type === SAFE_CLIENT_TYPES.MINOR)
      return currentQualification?.minor?.representative || {};
    if (
      type === SAFE_CLIENT_TYPES.UNBORN ||
      type === SAFE_CLIENT_TYPES.NASCITURO
    )
      return currentQualification?.unborn?.representative || {};
    if (type === SAFE_CLIENT_TYPES.BANKRUPTCY)
      return currentQualification?.bankruptcy?.administrator || {};
    if (type === SAFE_CLIENT_TYPES.CONDOMINIUM)
      return currentQualification?.condominium?.syndic || {};
    if (type === SAFE_CLIENT_TYPES.ESTATE)
      return currentQualification?.estate?.inventorier || {};
    if (type === SAFE_CLIENT_TYPES.IMPRISONED)
      return currentQualification?.imprisoned?.proxy || {};
    return {};
  })();

  const selectedRepresentative = selectedClient
    ? buildRepresentativeFromPhysicalClient(
        selectedClient,
        currentRepresentative,
      )
    : null;

  const repId = String(
    selectedRepresentative?.id ||
      currentAnchor?.id ||
      currentRepresentative?.id ||
      "",
  ).trim();
  const repName = String(
    selectedRepresentative?.name ||
      currentAnchor?.name ||
      currentRepresentative?.name ||
      "",
  ).trim();
  const repCpf = String(
    selectedRepresentative?.cpf ||
      currentAnchor?.cpf ||
      currentRepresentative?.cpf ||
      "",
  ).trim();

  if (!repId && !repName && !repCpf) return clientData;

  const mergedRepresentative = {
    ...(currentRepresentative || {}),
    ...(selectedRepresentative || {}),
    id: repId,
    name: repName,
    cpf: repCpf,
    address: {
      ...((currentRepresentative || {}).address || {}),
      ...((selectedRepresentative || {}).address || {}),
    },
    contact: {
      ...((currentRepresentative || {}).contact || {}),
      ...((selectedRepresentative || {}).contact || {}),
    },
  };

  const nextQualification = {
    ...(currentQualification || {}),
  };

  if (type === SAFE_CLIENT_TYPES.ANIMAL) {
    nextQualification.animal = {
      ...(currentQualification?.animal || {}),
      responsible: mergedRepresentative,
    };
  }
  if (type === SAFE_CLIENT_TYPES.LEGAL) {
    nextQualification.legal = {
      ...(currentQualification?.legal || {}),
      representative: mergedRepresentative,
    };
  }
  if (type === SAFE_CLIENT_TYPES.MINOR) {
    nextQualification.minor = {
      ...(currentQualification?.minor || {}),
      representative: mergedRepresentative,
    };
  }
  if (
    type === SAFE_CLIENT_TYPES.UNBORN ||
    type === SAFE_CLIENT_TYPES.NASCITURO
  ) {
    nextQualification.unborn = {
      ...(currentQualification?.unborn || {}),
      representative: mergedRepresentative,
    };
  }
  if (type === SAFE_CLIENT_TYPES.BANKRUPTCY) {
    nextQualification.bankruptcy = {
      ...(currentQualification?.bankruptcy || {}),
      administrator: mergedRepresentative,
    };
  }
  if (type === SAFE_CLIENT_TYPES.CONDOMINIUM) {
    nextQualification.condominium = {
      ...(currentQualification?.condominium || {}),
      syndic: mergedRepresentative,
    };
  }
  if (type === SAFE_CLIENT_TYPES.ESTATE) {
    nextQualification.estate = {
      ...(currentQualification?.estate || {}),
      inventorier: mergedRepresentative,
    };
  }
  if (type === SAFE_CLIENT_TYPES.IMPRISONED) {
    nextQualification.imprisoned = {
      ...(currentQualification?.imprisoned || {}),
      proxy: mergedRepresentative,
    };
  }

  return {
    ...clientData,
    anchor: {
      ...(currentAnchor || {}),
      id: repId,
      name: repName,
      cpf: repCpf,
      role: currentAnchor?.role || getDefaultAnchorRoleForType(type),
    },
    qualification: nextQualification,
  };
};

const clearRepresentativeLink = (clientData) => {
  const type = clientData?.type;
  const currentQualification = clientData?.qualification || {};
  const nextQualification = {
    ...(currentQualification || {}),
  };

  if (type === SAFE_CLIENT_TYPES.ANIMAL) {
    nextQualification.animal = {
      ...(currentQualification?.animal || {}),
      responsible: {},
    };
  }
  if (type === SAFE_CLIENT_TYPES.LEGAL) {
    nextQualification.legal = {
      ...(currentQualification?.legal || {}),
      representative: {},
    };
  }
  if (type === SAFE_CLIENT_TYPES.MINOR) {
    nextQualification.minor = {
      ...(currentQualification?.minor || {}),
      representative: {},
    };
  }
  if (
    type === SAFE_CLIENT_TYPES.UNBORN ||
    type === SAFE_CLIENT_TYPES.NASCITURO
  ) {
    nextQualification.unborn = {
      ...(currentQualification?.unborn || {}),
      representative: {},
    };
  }
  if (type === SAFE_CLIENT_TYPES.BANKRUPTCY) {
    nextQualification.bankruptcy = {
      ...(currentQualification?.bankruptcy || {}),
      administrator: {},
    };
  }
  if (type === SAFE_CLIENT_TYPES.CONDOMINIUM) {
    nextQualification.condominium = {
      ...(currentQualification?.condominium || {}),
      syndic: {},
    };
  }
  if (type === SAFE_CLIENT_TYPES.ESTATE) {
    nextQualification.estate = {
      ...(currentQualification?.estate || {}),
      inventorier: {},
    };
  }
  if (type === SAFE_CLIENT_TYPES.IMPRISONED) {
    nextQualification.imprisoned = {
      ...(currentQualification?.imprisoned || {}),
      proxy: {},
    };
  }

  return {
    ...clientData,
    anchor: null,
    qualification: nextQualification,
    __tutorLocked: false,
    livesWithAnchor: false,
    isFullDependent: false,
  };
};

const RoleBadge = ({ role }) => {
  const styles = {
    CLIENT: "bg-blue-100 text-blue-700 border-blue-300",
    PARTNER: "bg-purple-100 text-purple-700 border-purple-300",
    OTHER: "bg-gray-100 text-gray-700 border-slate-300",
  };
  const labels = { CLIENT: "Cliente", PARTNER: "Parceiro", OTHER: "Outro" };
  const safeRole = String(role || "OTHER").toUpperCase();
  return (
    <span
      className={`text-[9px] font-bold px-2 py-0.5 rounded-full border uppercase tracking-wide ${styles[safeRole] || styles.OTHER}`}
    >
      {labels[safeRole] || safeRole}
    </span>
  );
};

const getRoleLabel = (role) => {
  const safeRole = String(role || "")
    .trim()
    .toUpperCase();
  if (safeRole === "CLIENT") return "Cliente";
  if (safeRole === "PARTNER") return "Parceiro";
  if (safeRole === "OTHER") return "Outro";
  return safeRole || "Cliente";
};

const getClientEntityIconClass = (type) => {
  const safeType = String(type || "")
    .trim()
    .toUpperCase();
  if (safeType === "PESSOA FÍSICA" || safeType === "PHYSICAL")
    return "fas fa-user";
  if (
    safeType === "PESSOA JURÍDICA" ||
    safeType === "PESSOA JURIDICA" ||
    safeType === "LEGAL"
  )
    return "fas fa-building";
  if (
    safeType === "ANIMAL" ||
    safeType === "ANIMAL NÃO-HUMANO" ||
    safeType === "ANIMAL NAO-HUMANO"
  )
    return "fas fa-paw";
  if (safeType === "MENOR" || safeType === "MINOR") return "fas fa-child";
  if (safeType === "ESPÓLIO" || safeType === "ESPOLIO" || safeType === "ESTATE")
    return "fas fa-scroll";
  if (
    safeType === "CONDOMÍNIO" ||
    safeType === "CONDOMINIO" ||
    safeType === "CONDOMINIUM"
  )
    return "fas fa-city";
  return "fas fa-tag";
};

// ... (rest of the code remains the same)

const ConfigSection = ({
  sectionId,
  title,
  subtitle,
  icon,
  openSection,
  setOpenSection,
  rightSlot,
  children,
}) => {
  const isOpen = true;
  return (
    <div className="bg-white rounded-xl shadow-md border border-slate-300 overflow-hidden">
      <div className="w-full px-4 py-3 flex items-center justify-between gap-3 bg-gray-50">
        <div className="min-w-0 flex items-center gap-3">
          {icon ? <i className={`${icon} text-brand-blue text-sm`} /> : null}
          <div className="min-w-0 text-left">
            <div className="text-[11px] font-extrabold text-gray-700 uppercase tracking-widest truncate">
              {title}
            </div>
            {subtitle ? (
              <div className="text-[11px] text-gray-500 truncate">
                {subtitle}
              </div>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {rightSlot ? (
            <div className="hidden md:block">{rightSlot}</div>
          ) : null}
          <i className="fas fa-chevron-down text-gray-400 text-xs" />
        </div>
      </div>
      {isOpen ? <div className="p-4 md:p-6">{children}</div> : null}
    </div>
  );
};

// --- SERVIÇO DE ENRIQUECIMENTO ---
const EnrichmentService = {
  fetchCEP: async (cep) => {
    const cleanCep = cep.replace(/\D/g, "");
    if (cleanCep.length !== 8) throw new Error("CEP inválido");
    const res = await fetch(`https://brasilapi.com.br/api/cep/v2/${cleanCep}`);
    if (!res.ok) throw new Error("CEP não encontrado");
    return await res.json();
  },
  fetchCNPJ: async (cnpj) => {
    const cleanCnpj = cnpj.replace(/\D/g, "");
    if (cleanCnpj.length !== 14) throw new Error("CNPJ inválido");
    const res = await fetch(
      `https://brasilapi.com.br/api/cnpj/v1/${cleanCnpj}`,
    );
    if (!res.ok) throw new Error("CNPJ não encontrado");
    return await res.json();
  },
};

// --- FORMULÁRIO PRINCIPAL ---
const ClientForm = ({
  initialData,
  onSave,
  onCancel,
  onOpenClient,
  onCreateDependent,
  onDirtyChange,
}) => {
  const [formData, setFormData] = useState(() => {
    const base = JSON.parse(JSON.stringify(ClientSchema));
    base.roles = normalizeClientRoles(base.roles);
    base.professionalDocs = Array.isArray(base.professionalDocs)
      ? base.professionalDocs
      : [];
    base.anchor = null;
    base.__tutorLocked = false;
    const merged = mergeClientData(base, initialData);
    const normalizedMerged = normalizeRepresentativeLink(merged);
    normalizedMerged.roles = normalizeClientRoles(normalizedMerged.roles);
    normalizedMerged.professionalDocs = Array.isArray(
      normalizedMerged.professionalDocs,
    )
      ? normalizedMerged.professionalDocs
      : [];
    normalizedMerged.healthCondition = normalizeHealthCondition(
      normalizedMerged.healthCondition,
    );
    return normalizedMerged;
  });

  const [lastSavedData, setLastSavedData] = useState(null);

  const [loading, setLoading] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [errors, setErrors] = useState({});
  const [openSection, setOpenSection] = useState("IDENTIDADE");
  const [represented, setRepresented] = useState([]);
  const [representedLoading, setRepresentedLoading] = useState(false);

  const [spousePickerOpen, setSpousePickerOpen] = useState(false);

  const [naturalUF, setNaturalUF] = useState("");
  const [naturalCity, setNaturalCity] = useState("");
  const [citiesCache, setCitiesCache] = useState({});
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [conflictWarning, setConflictWarning] = useState(null);
  const [conflictLoading, setConflictLoading] = useState(false);

  const [oabResult, setOabResult] = useState(null);
  const [oabLoading, setOabLoading] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const showToast = useCallback((msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(""), 4000);
  }, []);
  const handleVerifyOAB = useCallback(async () => {
    const inscricao = String(
      formData?.qualification?.physical?.professionalRegister || "",
    ).trim();
    const uf = String(formData?.qualification?.physical?.registerUf || "")
      .trim()
      .toUpperCase();
    const nome = String(formData?.qualification?.physical?.name || "").trim();
    if (!inscricao && !nome) {
      setOabResult({ error: "Informe nome ou OAB do advogado." });
      showToast("Informe nome ou número da OAB para consultar.");
      return;
    }
    if (!uf) {
      setOabResult({ error: "Selecione a UF da OAB." });
      showToast("Selecione a UF do registro OAB.");
      return;
    }
    setOabLoading(true);
    setOabResult(null);
    try {
      const { LegalAPIs } = await import("../../../services/LegalAPIs");
      const result = await LegalAPIs.fetchOAB({ inscricao, uf, nome });
      setOabResult(result);
      if (result?.found) {
        const nomeOAB = result?.data?.nome || result?.data?.name || "";
        showToast(`✅ OAB válida — ${nomeOAB}`);
      } else if (result?.error) {
        showToast(`❌ ${result.error}`);
      } else {
        showToast("⚠️ Registro OAB não encontrado na base da OAB.");
      }
    } catch (err) {
      const msg = err?.message || "Falha ao consultar OAB";
      setOabResult({ error: msg });
      showToast(`❌ ${msg}`);
    } finally {
      setOabLoading(false);
    }
  }, [
    formData?.qualification?.physical?.professionalRegister,
    formData?.qualification?.physical?.registerUf,
    formData?.qualification?.physical?.name,
    showToast,
  ]);

  const handleSpouseSelect = (client) => {
    const name = client?.qualification?.physical?.name || "";
    const cpf = client?.qualification?.physical?.cpf || "";

    updateNestedField("qualification", "physical", "spouseName", name);
    updateNestedField("qualification", "physical", "spouseCPF", cpf);
    setSpousePickerOpen(false);
  };

  const [anchorSearch, setAnchorSearch] = useState("");
  const [anchorResults, setAnchorResults] = useState([]);
  const [anchorSearching, setAnchorSearching] = useState(false);
  const [anchorPickerOpen, setAnchorPickerOpen] = useState(false);
  const [showQuickAddAnchor, setShowQuickAddAnchor] = useState(false);
  const [showOtherIdDocs, setShowOtherIdDocs] = useState(false);
  const [cinEmissaoUF, setCinEmissaoUF] = useState("");
  const [cinEmissaoCity, setCinEmissaoCity] = useState("");
  const [quickAnchorName, setQuickAnchorName] = useState("");
  const [quickAnchorCpf, setQuickAnchorCpf] = useState("");
  const [creatingQuickAnchor, setCreatingQuickAnchor] = useState(false);

  const [anchorClient, setAnchorClient] = useState(null);
  const [inheritFromAnchor, setInheritFromAnchor] = useState(false);

  const typeIsPhysical = formData?.type === SAFE_CLIENT_TYPES.PHYSICAL;
  const age = typeIsPhysical
    ? calcAge(formData?.qualification?.physical?.birthDate)
    : null;
  const hasAgePriority = typeIsPhysical && typeof age === "number" && age >= 60;
  const hasSuperAgedPriority =
    typeIsPhysical && typeof age === "number" && age >= 80;
  const hasHealthPriority =
    normalizeHealthCondition(formData?.healthCondition).length > 0;
  const showHealthSection =
    formData?.type === SAFE_CLIENT_TYPES.PHYSICAL ||
    formData?.type === SAFE_CLIENT_TYPES.MINOR ||
    formData?.type === SAFE_CLIENT_TYPES.IMPRISONED ||
    formData?.type === SAFE_CLIENT_TYPES.UNBORN ||
    formData?.type === SAFE_CLIENT_TYPES.NASCITURO;
  const hasAnyPriority = Boolean(
    showHealthSection && (hasAgePriority || hasHealthPriority),
  );
  const needsAnchor = !typeIsPhysical || (typeof age === "number" && age < 18);
  const conflictProbeName = useMemo(() => {
    if (formData?.type === SAFE_CLIENT_TYPES.LEGAL) {
      return String(formData?.qualification?.legal?.companyName || "").trim();
    }
    return String(formData?.qualification?.physical?.name || "").trim();
  }, [
    formData?.qualification?.legal?.companyName,
    formData?.qualification?.physical?.name,
    formData?.type,
  ]);
  const conflictProbeDocument = useMemo(() => {
    if (formData?.type === SAFE_CLIENT_TYPES.LEGAL) {
      return String(formData?.qualification?.legal?.cnpj || "").trim();
    }
    return String(formData?.qualification?.physical?.cpf || "").trim();
  }, [
    formData?.qualification?.legal?.cnpj,
    formData?.qualification?.physical?.cpf,
    formData?.type,
  ]);

  useEffect(() => {
    const base = JSON.parse(JSON.stringify(ClientSchema));
    base.roles = normalizeClientRoles(base.roles);

    base.professionalDocs = Array.isArray(base.professionalDocs)
      ? base.professionalDocs
      : [];
    base.anchor = null;
    base.__tutorLocked = false;
    const merged = mergeClientData(base, initialData);
    const normalizedMerged = normalizeRepresentativeLink(merged);
    normalizedMerged.roles = normalizeClientRoles(normalizedMerged.roles);
    normalizedMerged.professionalDocs = Array.isArray(
      normalizedMerged.professionalDocs,
    )
      ? normalizedMerged.professionalDocs
      : [];
    normalizedMerged.healthCondition = normalizeHealthCondition(
      normalizedMerged.healthCondition,
    );
    setFormData(normalizedMerged);

    try {
      setLastSavedData(JSON.parse(JSON.stringify(normalizedMerged)));
    } catch {
      setLastSavedData(normalizedMerged);
    }
    setAnchorSearch(
      String(
        normalizedMerged?.anchor?.name ||
          normalizedMerged?.qualification?.animal?.responsible?.name ||
          "",
      ),
    );
    setAnchorResults([]);
    setAnchorPickerOpen(false);
    setErrors({});
    setShowQuickAddAnchor(false);
    setQuickAnchorName("");
    setQuickAnchorCpf("");
    setAnchorClient(null);
    setInheritFromAnchor(false);
    setConflictWarning(null);
    setConflictLoading(false);
  }, [initialData]);

  const isDirty = useMemo(() => {
    if (lastSavedData === null) return false;

    try {
      return JSON.stringify(formData) !== JSON.stringify(lastSavedData);
    } catch {
      return true;
    }
  }, [formData, lastSavedData]);

  useEffect(() => {
    if (typeof onDirtyChange !== "function") return;
    onDirtyChange(Boolean(isDirty));
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    const safeName = String(conflictProbeName || "").trim();
    const safeDocument = String(conflictProbeDocument || "").trim();
    const cleanDocument = safeDocument.replace(/\D/g, "");

    if (safeName.length < 3 && cleanDocument.length < 11) {
      setConflictWarning(null);
      setConflictLoading(false);
      return;
    }

    let alive = true;
    setConflictLoading(true);

    const timer = setTimeout(async () => {
      const summary = await scanForConflicts(safeName, safeDocument, {
        appId,
        excludeClientId: formData?.id,
      });

      if (!alive) return;
      setConflictWarning(summary?.hasConflict ? summary : null);
      setConflictLoading(false);
    }, 800);

    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [conflictProbeDocument, conflictProbeName, formData?.id]);

  useEffect(() => {
    if (formData?.type !== SAFE_CLIENT_TYPES.PHYSICAL) {
      setNaturalUF("");

      setNaturalCity("");
      return;
    }

    const raw = String(
      formData?.qualification?.physical?.naturalness || "",
    ).trim();
    const m = raw.match(/^\s*([^\/\-]+?)\s*[-\/]\s*([A-Za-z]{2})\s*$/);
    if (m) {
      setNaturalCity(String(m[1] || "").trim());
      setNaturalUF(
        String(m[2] || "")
          .trim()
          .toUpperCase(),
      );
    } else {
      // mantém o que estiver digitado sem forçar parse
      setNaturalCity(raw);
      setNaturalUF("");
    }
  }, [formData?.type, formData?.qualification?.physical?.naturalness]);

  useEffect(() => {
    if (formData?.type !== SAFE_CLIENT_TYPES.PHYSICAL) {
      setCinEmissaoUF("");
      setCinEmissaoCity("");
      return;
    }
    const raw = String(
      formData?.qualification?.physical?.cinLocalEmissao || "",
    ).trim();
    const m = raw.match(/^\s*([^\/\-]+?)\s*[-\/]\s*([A-Za-z]{2})\s*$/);
    if (m) {
      setCinEmissaoCity(String(m[1] || "").trim());
      setCinEmissaoUF(
        String(m[2] || "")
          .trim()
          .toUpperCase(),
      );
    } else {
      setCinEmissaoCity(raw);
      setCinEmissaoUF("");
    }
  }, [formData?.type, formData?.qualification?.physical?.cinLocalEmissao]);

  useEffect(() => {
    // Cache compartilhado por UF — qualquer seletor UF->Cidade do formulário
    // (Naturalidade, Local de Emissão da CIN, etc.) alimenta e consome o
    // mesmo `citiesCache`, sem duplicar a chamada à API por campo.
    const ufsInUse = [naturalUF, cinEmissaoUF]
      .map((v) => String(v || "").trim().toUpperCase())
      .filter((u) => u && !citiesCache?.[u]);
    if (ufsInUse.length === 0) return;

    let alive = true;
    const run = async () => {
      try {
        setCitiesLoading(true);
        const results = await Promise.all(
          ufsInUse.map((u) => LegalAPIs.fetchCities(u)),
        );
        if (!alive) return;
        setCitiesCache((prev) => {
          const next = { ...(prev || {}) };
          ufsInUse.forEach((u, i) => {
            next[u] = results[i];
          });
          return next;
        });
      } catch (e) {
        console.error(e);
      } finally {
        if (alive) setCitiesLoading(false);
      }
    };
    run();
    return () => {
      alive = false;
    };
  }, [naturalUF, cinEmissaoUF, citiesCache]);

  useEffect(() => {
    if (!formData?.isFullDependent) return;
    setInheritFromAnchor(true);
    setFormData((prev) => ({ ...prev, livesWithAnchor: true }));
  }, [formData?.isFullDependent]);

  useEffect(() => {
    if (!inheritFromAnchor) return;
    if (!anchorClient) return;

    setFormData((prev) => {
      const next = { ...prev };
      next.contact = {
        ...next.contact,
        email: anchorClient?.contact?.email || next.contact?.email || "",
        phonePrimary:
          anchorClient?.contact?.phonePrimary ||
          next.contact?.phonePrimary ||
          "",
      };
      next.location = {
        ...next.location,
        address: {
          ...next.location?.address,
          ...(anchorClient?.location?.address || {}),
        },
      };
      return next;
    });
  }, [inheritFromAnchor, anchorClient]);

  useEffect(() => {
    if (!formData?.livesWithAnchor) return;
    if (!anchorClient) return;
    setFormData((prev) => ({
      ...prev,
      location: {
        ...prev.location,
        address: {
          ...prev.location?.address,
          ...(anchorClient?.location?.address || {}),
        },
      },
    }));
  }, [formData?.livesWithAnchor, anchorClient]);

  useEffect(() => {
    const currentId = formData?.id || initialData?.id;
    if (!currentId || formData?.type !== SAFE_CLIENT_TYPES.PHYSICAL) {
      setRepresented([]);
      return;
    }

    const fetchRepresented = async () => {
      setRepresentedLoading(true);
      try {
        const data = await repo.fetchRepresented(appId, currentId);
        setRepresented(data);
      } catch (e) {
        console.error("[Tutela] fetchRepresented fatal error:", e);
        setRepresented([]);
      } finally {
        setRepresentedLoading(false);
      }
    };

    fetchRepresented();
  }, [formData?.id, initialData?.id, formData?.type]);

  useEffect(() => {
    if (!needsAnchor) {
      setAnchorResults([]);
      return;
    }

    const term = String(anchorSearch || "")
      .trim()
      .toLowerCase();
    if (term.length < 2) {
      setAnchorResults([]);
      return;
    }

    const run = async () => {
      setAnchorSearching(true);
      try {
        const data = await repo.searchAnchors(
          appId,
          term,
          SAFE_CLIENT_TYPES.PHYSICAL,
        );
        setAnchorResults(data);
      } catch (e) {
        console.warn("[Anchor] Search failed:", e);
        setAnchorResults([]);
      } finally {
        setAnchorSearching(false);
      }
    };

    const t = setTimeout(run, 300);
    return () => clearTimeout(t);
  }, [anchorSearch, needsAnchor]);

  useEffect(() => {
    const anchorId = String(formData?.anchor?.id || "").trim();
    if (!needsAnchor || !anchorId) {
      setAnchorClient(null);
      return;
    }

    if (String(anchorClient?.id || "").trim() === anchorId) return;

    let alive = true;
    const run = async () => {
      try {
        const data = await repo.fetchAnchorClient(appId, anchorId);
        if (!alive) return;
        setAnchorClient(data);
      } catch (e) {
        if (!alive) return;
        console.warn("[Anchor] Failed to rehydrate linked responsible:", e);
        setAnchorClient(null);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [formData?.anchor?.id, needsAnchor, anchorClient?.id]);

  const sortedClientTypes = useMemo(() => {
    if (!CLIENT_TYPES) return [];
    const types = Object.entries(CLIENT_TYPES);
    const physical = types.find(([k]) => k === "PHYSICAL");
    const others = types
      .filter(([k]) => k !== "PHYSICAL")
      .sort((a, b) => a[1].localeCompare(b[1]));
    return physical ? [physical, ...others] : others;
  }, []);

  const updateNestedField = (section, subsection, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [subsection]: { ...prev[section][subsection], [field]: value },
      },
    }));
    if (errors[`${section}.${subsection}.${field}`])
      setErrors((prev) => {
        const n = { ...prev };
        delete n[`${section}.${subsection}.${field}`];
        return n;
      });
  };

  const updateRootField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field])
      setErrors((prev) => {
        const n = { ...prev };
        delete n[field];
        return n;
      });
  };

  const updateAnimalCoatField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      qualification: {
        ...(prev.qualification || {}),
        animal: {
          ...(prev.qualification?.animal || {}),
          coat: {
            ...((prev.qualification?.animal || {}).coat || {}),
            [field]: value,
          },
        },
      },
    }));
  };

  const updatePhysicalWorkCardField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      qualification: {
        ...(prev.qualification || {}),
        physical: {
          ...(prev.qualification?.physical || {}),
          carteiraTrabalho: {
            ...((prev.qualification?.physical || {}).carteiraTrabalho || {}),
            [field]: value,
          },
        },
      },
    }));
  };

  const updatePhysicalElectoralField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      qualification: {
        ...(prev.qualification || {}),
        physical: {
          ...(prev.qualification?.physical || {}),
          electoral: {
            ...((prev.qualification?.physical || {}).electoral || {}),
            [field]: value,
          },
        },
      },
    }));
  };

  const updateAnimalArrayField = (field, value) => {
    const normalized = String(value || "")
      .split(";")
      .map((item) => item.trim())
      .filter(Boolean);
    updateNestedField("qualification", "animal", field, normalized);
  };

  const docTypeOptions = [
    { value: "OAB", label: "OAB (Advogado)" },
    { value: "CRM", label: "CRM (Médico)" },
    { value: "CREA", label: "CREA (Engenheiro)" },
    { value: "CAU", label: "CAU (Arquiteto)" },
    { value: "CRC", label: "CRC (Contador)" },
    { value: "COREN", label: "COREN (Enfermagem)" },
    { value: "ID_MILITAR", label: "Id. Militar" },
    { value: "PASSAPORTE", label: "Passaporte" },
    { value: "OUTRO", label: "Outro" },
  ];

  const docStateOptions = [
    "BR",
    "AC",
    "AL",
    "AP",
    "AM",
    "BA",
    "CE",
    "DF",
    "ES",
    "GO",
    "MA",
    "MT",
    "MS",
    "MG",
    "PA",
    "PB",
    "PR",
    "PE",
    "PI",
    "RJ",
    "RN",
    "RS",
    "RO",
    "RR",
    "SC",
    "SP",
    "SE",
    "TO",
  ];

  const handleAddDoc = () => {
    setFormData((prev) => ({
      ...prev,
      professionalDocs: [
        ...(prev.professionalDocs || []),
        { type: "OAB", number: "", state: "PR" },
      ],
    }));
  };

  const handleRemoveDoc = (index) => {
    setFormData((prev) => ({
      ...prev,
      professionalDocs: (prev.professionalDocs || []).filter(
        (_, i) => i !== index,
      ),
    }));
  };

  const handleDocChange = (index, field, value) => {
    const newDocs = [...(formData.professionalDocs || [])];
    newDocs[index] = { ...(newDocs[index] || {}), [field]: value };
    setFormData((prev) => ({ ...prev, professionalDocs: newDocs }));
  };

  const updateField = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
    if (errors[`${section}.${field}`])
      setErrors((prev) => {
        const n = { ...prev };
        delete n[`${section}.${field}`];
        return n;
      });
  };

  const handleBlurValidation = (field, value, type) => {
    let error = null;
    if (type === "CPF" && value && !Validator.cpf(value))
      error = "CPF Inválido";
    if (type === "CNPJ" && value && !Validator.cnpj(value))
      error = "CNPJ Inválido";
    if (type === "EMAIL" && value && !Validator.email(value))
      error = "E-mail inválido";
    if (type === "PHONE" && value && !Validator.phone(value))
      error = "Telefone incompleto";
    if (error) setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleCepSearch = async () => {
    setEnriching(true);
    try {
      const data = await EnrichmentService.fetchCEP(
        formData.location.address.zipCode,
      );
      setFormData((prev) => ({
        ...prev,
        location: {
          ...prev.location,
          address: {
            ...prev.location.address,
            street: data.street,
            neighborhood: data.neighborhood,
            city: data.city,
            state: data.state,
          },
        },
      }));
    } catch (e) {
      alert(e.message);
    } finally {
      setEnriching(false);
    }
  };

  const handleCnpjSearch = async () => {
    setEnriching(true);
    try {
      const data = await EnrichmentService.fetchCNPJ(
        formData.qualification.legal.cnpj,
      );
      let repData = {};
      let repRole = "Sócio-Administrador";
      if (data.qsa && data.qsa.length > 0) {
        repData = { name: data.qsa[0].nome_socio };
        repRole = data.qsa[0].qualificacao_socio || repRole;
      }
      setFormData((prev) => ({
        ...prev,
        qualification: {
          ...prev.qualification,
          legal: {
            ...prev.qualification.legal,
            companyName: data.razao_social,
            tradeName: data.nome_fantasia || data.razao_social,
            representativeRole: repRole,
            representative: {
              ...prev.qualification.legal.representative,
              ...repData,
            },
          },
        },
        location: {
          ...prev.location,
          address: {
            ...prev.location.address,
            street: data.logradouro,
            number: data.numero,
            neighborhood: data.bairro,
            city: data.municipio,
            state: data.uf,
            zipCode: data.cep.replace(".", "").replace("-", ""),
          },
        },
      }));
    } catch (e) {
      alert(e.message);
    } finally {
      setEnriching(false);
    }
  };

  const validateForm = (data = formData) => {
    const newErrors = {};
    const type = data.type;

    if (!data?.isFullDependent) {
      if (!data.contact.email) newErrors["contact.email"] = "Obrigatório";
      else if (!Validator.email(data.contact.email))
        newErrors["contact.email"] = "Inválido";

      if (!data.contact.phonePrimary)
        newErrors["contact.phonePrimary"] = "Obrigatório";
    }

    if (type === SAFE_CLIENT_TYPES.PHYSICAL) {
      if (!data.qualification.physical.name)
        newErrors["qualification.physical.name"] = "Obrigatório";
      if (!data.qualification.physical.cpf)
        newErrors["qualification.physical.cpf"] = "Obrigatório";
      else if (!Validator.cpf(data.qualification.physical.cpf))
        newErrors["qualification.physical.cpf"] = "Inválido";
    }

    if (type === SAFE_CLIENT_TYPES.LEGAL) {
      if (!data.qualification.legal.companyName)
        newErrors["qualification.legal.companyName"] = "Obrigatório";
      if (!data.qualification.legal.cnpj)
        newErrors["qualification.legal.cnpj"] = "Obrigatório";
    }

    if (
      type === SAFE_CLIENT_TYPES.UNBORN ||
      type === SAFE_CLIENT_TYPES.NASCITURO
    ) {
      if (!data.qualification?.unborn?.mothersName)
        newErrors["qualification.unborn.mothersName"] = "Obrigatório";
    }

    // A3 — Art. 319 CPC: endereço obrigatório para PF e PJ
    if (
      type === SAFE_CLIENT_TYPES.PHYSICAL ||
      type === SAFE_CLIENT_TYPES.LEGAL
    ) {
      if (!data?.location?.address?.city)
        newErrors["location.address.city"] = "Obrigatório (Art. 319 CPC)";
      if (!data?.location?.address?.state)
        newErrors["location.address.state"] = "Obrigatório (Art. 319 CPC)";
    }

    // A2 — Validação estrita de menores
    if (type === SAFE_CLIENT_TYPES.MINOR) {
      if (!data.qualification?.minor?.name)
        newErrors["qualification.minor.name"] = "Obrigatório (nome do menor)";
      const minorCpf = String(data.qualification?.minor?.cpf || "").replace(
        /\D/g,
        "",
      );
      if (minorCpf && minorCpf.length < 11)
        newErrors["qualification.minor.cpf"] = "CPF inválido (11 dígitos)";
      if (!data?.anchor?.id)
        newErrors["anchor.id"] =
          "Representante legal obrigatório (pai/mãe/tutor)";
    }

    if (type === SAFE_CLIENT_TYPES.BANKRUPTCY) {
      if (!data.qualification?.bankruptcy?.companyName)
        newErrors["qualification.bankruptcy.companyName"] = "Obrigatório";
      if (!data.qualification?.bankruptcy?.cnpj)
        newErrors["qualification.bankruptcy.cnpj"] = "Obrigatório";
    }

    if (!data?.clientOrigin) {
      newErrors["clientOrigin"] = "Obrigatório (origem do lead)";
    }

    // Permite update de cliente existente mesmo sem anchor.id no estado do formulário
    // (o anchor já foi salvo no Firestore no cadastro original)
    if (needsAnchor && !data?.id) {
      if (!data.anchor?.id) newErrors["anchor.id"] = "Obrigatório";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSelectAnchor = (client) => {
    if (!client?.id) return;
    const name =
      client?.qualification?.physical?.name ||
      client?.qualificationText ||
      "Responsável";
    const cpf = client?.qualification?.physical?.cpf || "";

    setFormData((prev) => {
      const withAnchor = {
        ...prev,
        anchor: {
          ...(prev?.anchor || {}),
          id: String(client.id),
          name: String(name),
          cpf: String(cpf),
          role: prev?.anchor?.role || getDefaultAnchorRoleForType(prev?.type),
        },
        __tutorLocked: true,
      };
      return normalizeRepresentativeLink(withAnchor, client);
    });
    setAnchorClient(client);
    setAnchorSearch(String(name));
    setAnchorResults([]);
    setAnchorPickerOpen(false);
    setErrors((prev) => {
      const next = { ...(prev || {}) };
      delete next["anchor.id"];
      return next;
    });
  };

  const handleClearAnchor = () => {
    setFormData((prev) => clearRepresentativeLink(prev));
    setAnchorClient(null);
    setAnchorSearch("");
    setAnchorResults([]);
    setAnchorPickerOpen(false);
    setErrors((prev) => {
      const next = { ...(prev || {}) };
      delete next["anchor.id"];
      return next;
    });
  };

  const handleCreateQuickAnchor = async () => {
    const name = String(quickAnchorName || "").trim();
    const cpf = String(quickAnchorCpf || "").trim();
    if (!name) return alert("Informe o nome do responsável.");
    if (!cpf) return alert("Informe o CPF do responsável.");
    if (!Validator.cpf(cpf)) return alert("CPF inválido.");

    setCreatingQuickAnchor(true);
    try {
      const physical = JSON.parse(JSON.stringify(ClientSchema));
      physical.type = SAFE_CLIENT_TYPES.PHYSICAL;
      physical.qualification.physical.name = name;
      physical.qualification.physical.cpf = cpf;
      physical.updatedAt = repo.serverTimestamp();
      physical.qualificationText = generateQualificationText(physical);
      physical.searchKeywords = SearchEngine.createClientIndex(physical);

      const result = await repo.createQuickAnchor(appId, physical);
      handleSelectAnchor(result);
      setShowQuickAddAnchor(false);
      setQuickAnchorName("");
      setQuickAnchorCpf("");
    } catch (e) {
      alert("Erro ao criar responsável: " + e.message);
    } finally {
      setCreatingQuickAnchor(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let payload = normalizeRepresentativeLink(formData, anchorClient);

    const payloadShowHealthSection =
      payload?.type === SAFE_CLIENT_TYPES.PHYSICAL ||
      payload?.type === SAFE_CLIENT_TYPES.MINOR ||
      payload?.type === SAFE_CLIENT_TYPES.IMPRISONED ||
      payload?.type === SAFE_CLIENT_TYPES.UNBORN ||
      payload?.type === SAFE_CLIENT_TYPES.NASCITURO;
    if (
      !payloadShowHealthSection &&
      payload?.healthCondition &&
      payload.healthCondition !== "NENHUMA"
    ) {
      payload = { ...payload, healthCondition: [] };
    }

    const forcedPronoun =
      formData?.type === SAFE_CLIENT_TYPES.LEGAL
        ? "A"
        : formData?.type === SAFE_CLIENT_TYPES.ESTATE
          ? "O"
          : formData?.type === SAFE_CLIENT_TYPES.UNBORN ||
              formData?.type === SAFE_CLIENT_TYPES.NASCITURO
            ? "O"
            : null;

    if (forcedPronoun) {
      payload = {
        ...payload,
        qualification: {
          ...(payload.qualification || {}),
          physical: {
            ...((payload.qualification || {}).physical || {}),
            pronoun: forcedPronoun,
          },
        },
      };
    }

    payload = {
      ...payload,
      roles: normalizeClientRoles(payload?.roles),
    };

    if (formData?.isFullDependent) {
      if (!anchorClient) {
        return alert(
          "Selecione o Tutor/Responsável antes de ativar a Dependência Total.",
        );
      }
      payload = {
        ...payload,
        livesWithAnchor: true,
        contact: {
          ...payload.contact,
          email: anchorClient?.contact?.email || "",
          phonePrimary: anchorClient?.contact?.phonePrimary || "",
        },
        location: {
          ...payload.location,
          address: {
            ...payload.location?.address,
            ...(anchorClient?.location?.address || {}),
          },
        },
      };
    }

    if (!validateForm(payload)) return alert("Corrija os erros destacados.");
    // Botão "Salvar" (fica aberto) vs "Salvar e Fechar" (padrão) — o
    // submitter identifica qual dos dois <button type="submit"> foi
    // clicado (Enter no formulário sempre aciona o botão padrão, sem
    // "intent", então mantém o fechamento de sempre). Pedido do usuário
    // 24/08/2026: irritação real de ter que reabrir o cadastro pra cada
    // pequeno ajuste.
    const keepOpen = e?.nativeEvent?.submitter?.value === "keep-open";
    setLoading(true);
    await onSave(payload, { keepOpen });
    setLoading(false);

    try {
      setLastSavedData(JSON.parse(JSON.stringify(payload)));
    } catch {
      setLastSavedData(payload);
    }
  };

  const renderTypeSpecificFields = () => {
    const type = formData.type;
    const q = formData.qualification || {};
    const safeQ = {
      physical: q.physical || {},
      legal: q.legal || {},
      minor: q.minor || {},
      animal: q.animal || {},
    };

    switch (type) {
      case SAFE_CLIENT_TYPES.PHYSICAL:
        return (
          <>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="md:col-span-4">
              <LegalLabel icon="fa-user" required>
                Nome Civil{" "}
                <InfoTooltip text="Digite exatamente como consta no RG/CNH. Usado em contratos e processos judiciais." />
              </LegalLabel>
              <LegalInput
                value={safeQ.physical.name}
                onChange={(v) =>
                  updateNestedField("qualification", "physical", "name", v)
                }
                error={errors["qualification.physical.name"]}
              />
            </div>
            <div className="md:col-span-4">
              <LegalLabel icon="fa-id-badge">
                Nome Social{" "}
                <InfoTooltip text="Nome pelo qual a pessoa quer ser chamada. Se for igual ao do documento de identificação, deixe em branco." />
              </LegalLabel>
              <LegalInput
                value={safeQ.physical.socialName || ""}
                onChange={(v) =>
                  updateNestedField(
                    "qualification",
                    "physical",
                    "socialName",
                    v,
                  )
                }
                placeholder="Nome social (como prefere ser chamado)"
              />
            </div>
            <div className="md:col-span-1">
              <LegalLabel icon="fa-person-half-dress">Tratamento</LegalLabel>
              <LegalSelect
                value={safeQ.physical.pronoun || ""}
                onChange={(e) =>
                  updateNestedField(
                    "qualification",
                    "physical",
                    "pronoun",
                    e.target.value,
                  )
                }
              >
                <option value="">Não informado</option>
                <option value="Sr. (O)">Sr. (O)</option>
                <option value="Sra. (A)">Sra. (A)</option>
                <option value="Dr.">Dr.</option>
                <option value="Dra.">Dra.</option>
                <option value="Neutro">Neutro</option>
              </LegalSelect>
            </div>
            <div className="md:col-span-1">
              <LegalLabel icon="fa-venus-mars">
                Gênero Gramatical
                <InfoTooltip text="M = Masculino (cliente/o). F = Feminino (cliente/a). Usado em contratos e petições para concordância verbal." />
              </LegalLabel>
              <LegalSelect
                value={safeQ.physical.grammaticalGender || ""}
                onChange={(e) =>
                  updateNestedField(
                    "qualification",
                    "physical",
                    "grammaticalGender",
                    e.target.value,
                  )
                }
              >
                <option value="">Não informado</option>
                <option value="M">Masculino (M)</option>
                <option value="F">Feminino (F)</option>
              </LegalSelect>
            </div>
            <div className="md:col-span-2">
              <LegalLabel icon="fa-id-card" required>
                CPF
              </LegalLabel>
              <InputMasked
                mask="cpf"
                value={safeQ.physical.cpf}
                onChange={(v) =>
                  updateNestedField("qualification", "physical", "cpf", v)
                }
                onBlur={() =>
                  handleBlurValidation(
                    "qualification.physical.cpf",
                    safeQ.physical.cpf,
                    "CPF",
                  )
                }
                error={errors["qualification.physical.cpf"]}
              />
            </div>
            <div className="md:col-span-2">
              <LegalLabel icon="fa-id-badge">
                Tipo de Documento de Identidade
                <InfoTooltip text="RG tradicional (emitido pela SSP/instituto de identificação estadual, número próprio) ou Carteira de Identidade Nacional — CIN (documento unificado gov.br, cujo número de registro é o próprio CPF)." />
              </LegalLabel>
              <LegalSelect
                value={safeQ.physical.identityDocType || "RG"}
                onChange={(e) =>
                  updateNestedField(
                    "qualification",
                    "physical",
                    "identityDocType",
                    e.target.value,
                  )
                }
              >
                <option value="RG">RG tradicional</option>
                <option value="CIN">
                  Carteira de Identidade Nacional (CIN)
                </option>
              </LegalSelect>
            </div>
            {(safeQ.physical.identityDocType || "RG") === "CIN" ? (
              <>
                <div className="md:col-span-2">
                  <LegalLabel>
                    Local de Emissão (UF / Cidade)
                    <InfoTooltip text="Escolher o estado primeiro auto-preenche o Órgão Expedidor quando já soubermos o nome dele (crescimento orgânico — só entra estado confirmado por caso real, nunca por achismo)." />
                  </LegalLabel>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                    <div className="md:col-span-1">
                      <LegalSelect
                        value={cinEmissaoUF}
                        onChange={(e) => {
                          const uf = String(
                            e.target.value || "",
                          ).toUpperCase();
                          setCinEmissaoUF(uf);
                          setCinEmissaoCity("");
                          updateNestedField(
                            "qualification",
                            "physical",
                            "cinLocalEmissao",
                            uf ? `/${uf}` : "",
                          );
                          const knownOrgao =
                            SAFE_MASTER_DATA.CIN_ORGAO_EXPEDIDOR_POR_UF[uf];
                          if (
                            knownOrgao &&
                            !safeQ.physical.cinOrgaoExpedidor
                          ) {
                            updateNestedField(
                              "qualification",
                              "physical",
                              "cinOrgaoExpedidor",
                              knownOrgao,
                            );
                          }
                        }}
                      >
                        <option value="">UF</option>
                        {SAFE_MASTER_DATA.BRAZIL_STATES.map((uf) => (
                          <option key={uf} value={uf}>
                            {uf}
                          </option>
                        ))}
                      </LegalSelect>
                    </div>
                    <div className="md:col-span-3">
                      <LegalSelect
                        value={cinEmissaoCity}
                        onChange={(e) => {
                          const city = String(e.target.value || "");
                          setCinEmissaoCity(city);
                          updateNestedField(
                            "qualification",
                            "physical",
                            "cinLocalEmissao",
                            city && cinEmissaoUF
                              ? `${city}/${cinEmissaoUF}`
                              : city,
                          );
                        }}
                        disabled={!cinEmissaoUF || citiesLoading}
                      >
                        <option value="">
                          {citiesLoading ? "Carregando..." : "Cidade"}
                        </option>
                        {(Array.isArray(citiesCache?.[cinEmissaoUF])
                          ? citiesCache[cinEmissaoUF]
                          : []
                        ).map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </LegalSelect>
                    </div>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <LegalLabel>
                    Órgão Expedidor (CIN)
                    <InfoTooltip text="Ex.: Instituto de Identificação do Paraná — consta no verso da CIN, campo 'Órgão Expedidor / Card Issuer'. Auto-preenchido quando já soubermos o órgão do estado escolhido; edite livremente se estiver errado ou faltando." />
                  </LegalLabel>
                  <LegalHybridInput
                    listId="org-phys-cin"
                    options={SAFE_MASTER_DATA.ORG_EMISSORES}
                    value={safeQ.physical.cinOrgaoExpedidor || ""}
                    onChange={(e) =>
                      updateNestedField(
                        "qualification",
                        "physical",
                        "cinOrgaoExpedidor",
                        e.target.value,
                      )
                    }
                    placeholder="Instituto de Identificação do Paraná"
                  />
                </div>
                <div className="md:col-span-1">
                  <LegalLabel>
                    Data de Emissão
                    <InfoTooltip text="Só fica salva no cadastro — não entra no texto de qualificação de procurações/petições, sem efeito jurídico ali." />
                  </LegalLabel>
                  <LegalInput
                    type="date"
                    value={safeQ.physical.cinDataEmissao || ""}
                    onChange={(v) =>
                      updateNestedField(
                        "qualification",
                        "physical",
                        "cinDataEmissao",
                        v,
                      )
                    }
                  />
                </div>
                <div className="md:col-span-1">
                  <LegalLabel>
                    Data de Validade
                    <InfoTooltip text="Só fica salva no cadastro — não entra no texto de qualificação de procurações/petições, sem efeito jurídico ali." />
                  </LegalLabel>
                  <LegalInput
                    type="date"
                    value={safeQ.physical.cinDataValidade || ""}
                    onChange={(v) =>
                      updateNestedField(
                        "qualification",
                        "physical",
                        "cinDataValidade",
                        v,
                      )
                    }
                  />
                </div>
              </>
            ) : (
              <>
                <div className="md:col-span-2">
                  <LegalLabel>RG</LegalLabel>
                  <InputMasked
                    mask="rg"
                    value={safeQ.physical.rg}
                    onChange={(v) =>
                      updateNestedField("qualification", "physical", "rg", v)
                    }
                  />
                </div>
                <div className="md:col-span-2">
                  <LegalLabel>Órgão Emissor</LegalLabel>
                  <LegalHybridInput
                    listId="org-phys"
                    options={SAFE_MASTER_DATA.ORG_EMISSORES}
                    value={safeQ.physical.orgaoEmissor}
                    onChange={(e) =>
                      updateNestedField(
                        "qualification",
                        "physical",
                        "orgaoEmissor",
                        e.target.value,
                      )
                    }
                    placeholder="SSP"
                  />
                </div>
              </>
            )}
          </div>
          <div className="mt-4 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={() => setShowOtherIdDocs((prev) => !prev)}
              className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500 hover:text-brand-blue transition-colors"
            >
              <i
                className={`fas fa-chevron-${showOtherIdDocs ? "down" : "right"} text-[10px]`}
              ></i>
              Outros Documentos (CNH, PIS/PASEP, NIS, NIT, Carteira de
              Trabalho, Título de Eleitor)
            </button>
            {showOtherIdDocs && (
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mt-3">
                <div className="md:col-span-2">
                  <LegalLabel icon="fa-id-card">CNH</LegalLabel>
                  <LegalInput
                    value={safeQ.physical.cnh || ""}
                    onChange={(v) =>
                      updateNestedField("qualification", "physical", "cnh", v)
                    }
                  />
                </div>
                <div className="md:col-span-1">
                  <LegalLabel>Categoria</LegalLabel>
                  <LegalInput
                    value={safeQ.physical.cnhCategoria || ""}
                    onChange={(v) =>
                      updateNestedField(
                        "qualification",
                        "physical",
                        "cnhCategoria",
                        v,
                      )
                    }
                    placeholder="AB"
                  />
                </div>
                <div className="md:col-span-2">
                  <LegalLabel>PIS/PASEP</LegalLabel>
                  <LegalInput
                    value={safeQ.physical.pisPasep || ""}
                    onChange={(v) =>
                      updateNestedField(
                        "qualification",
                        "physical",
                        "pisPasep",
                        v,
                      )
                    }
                  />
                </div>
                <div className="md:col-span-1">
                  <LegalLabel>NIS</LegalLabel>
                  <LegalInput
                    value={safeQ.physical.nis || ""}
                    onChange={(v) =>
                      updateNestedField("qualification", "physical", "nis", v)
                    }
                  />
                </div>
                <div className="md:col-span-2">
                  <LegalLabel>NIT</LegalLabel>
                  <LegalInput
                    value={safeQ.physical.nit || ""}
                    onChange={(v) =>
                      updateNestedField("qualification", "physical", "nit", v)
                    }
                  />
                </div>
                <div className="md:col-span-2">
                  <LegalLabel>Carteira de Trabalho — Número</LegalLabel>
                  <LegalInput
                    value={safeQ.physical.carteiraTrabalho?.numero || ""}
                    onChange={(v) => updatePhysicalWorkCardField("numero", v)}
                  />
                </div>
                <div className="md:col-span-1">
                  <LegalLabel>Série</LegalLabel>
                  <LegalInput
                    value={safeQ.physical.carteiraTrabalho?.serie || ""}
                    onChange={(v) => updatePhysicalWorkCardField("serie", v)}
                  />
                </div>
                <div className="md:col-span-1">
                  <LegalLabel>UF</LegalLabel>
                  <LegalInput
                    value={safeQ.physical.carteiraTrabalho?.uf || ""}
                    onChange={(v) => updatePhysicalWorkCardField("uf", v)}
                    placeholder="PR"
                  />
                </div>
                <div className="md:col-span-2">
                  <LegalLabel>Título de Eleitor — Número</LegalLabel>
                  <LegalInput
                    value={safeQ.physical.electoral?.voterId || ""}
                    onChange={(v) => updatePhysicalElectoralField("voterId", v)}
                  />
                </div>
                <div className="md:col-span-1">
                  <LegalLabel>Zona</LegalLabel>
                  <LegalInput
                    value={safeQ.physical.electoral?.zone || ""}
                    onChange={(v) => updatePhysicalElectoralField("zone", v)}
                  />
                </div>
                <div className="md:col-span-1">
                  <LegalLabel>Seção</LegalLabel>
                  <LegalInput
                    value={safeQ.physical.electoral?.section || ""}
                    onChange={(v) => updatePhysicalElectoralField("section", v)}
                  />
                </div>
              </div>
            )}
          </div>
          </>
        );
      case SAFE_CLIENT_TYPES.IMPRISONED:
        return (
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="md:col-span-4">
              <LegalLabel icon="fa-user-lock" required>
                Nome
              </LegalLabel>
              <LegalInput
                value={safeQ.imprisoned.name || ""}
                onChange={(v) =>
                  updateNestedField("qualification", "imprisoned", "name", v)
                }
              />
            </div>
            <div className="md:col-span-2">
              <LegalLabel icon="fa-id-card" required>
                CPF
              </LegalLabel>
              <InputMasked
                mask="cpf"
                value={safeQ.imprisoned.cpf || ""}
                onChange={(v) =>
                  updateNestedField("qualification", "imprisoned", "cpf", v)
                }
              />
            </div>
            <div className="md:col-span-2">
              <LegalLabel>RG</LegalLabel>
              <InputMasked
                mask="rg"
                value={safeQ.imprisoned.rg || ""}
                onChange={(v) =>
                  updateNestedField("qualification", "imprisoned", "rg", v)
                }
              />
            </div>
            <div className="md:col-span-2">
              <LegalLabel>Matrícula / INFOPEN</LegalLabel>
              <LegalInput
                value={safeQ.imprisoned.matriculaPrisional || ""}
                onChange={(v) =>
                  updateNestedField(
                    "qualification",
                    "imprisoned",
                    "matriculaPrisional",
                    v,
                  )
                }
                placeholder="Ex: 12345-6"
              />
            </div>
            <div className="md:col-span-2">
              <LegalLabel>Unidade Prisional</LegalLabel>
              <LegalInput
                value={safeQ.imprisoned.prisonUnit || ""}
                onChange={(v) =>
                  updateNestedField(
                    "qualification",
                    "imprisoned",
                    "prisonUnit",
                    v,
                  )
                }
              />
            </div>
            <div className="md:col-span-2">
              <LegalLabel>Cidade/UF (Unidade)</LegalLabel>
              <LegalInput
                value={safeQ.imprisoned.cityUnit || ""}
                onChange={(v) =>
                  updateNestedField(
                    "qualification",
                    "imprisoned",
                    "cityUnit",
                    v,
                  )
                }
                placeholder="Ex: Curitiba/PR"
              />
            </div>
          </div>
        );
      case SAFE_CLIENT_TYPES.LEGAL:
        return (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <LegalLabel icon="fa-building" required>
                  CNPJ
                </LegalLabel>
                <div className="flex gap-2">
                  <InputMasked
                    mask="cnpj"
                    value={safeQ.legal.cnpj}
                    onChange={(v) =>
                      updateNestedField("qualification", "legal", "cnpj", v)
                    }
                    error={errors["qualification.legal.cnpj"]}
                  />
                  <button
                    type="button"
                    onClick={handleCnpjSearch}
                    className="bg-brand-blue text-white px-3 rounded-lg"
                  >
                    <i
                      className={`fas ${enriching ? "fa-spinner fa-spin" : "fa-search"}`}
                    ></i>
                  </button>
                </div>
              </div>
              <div className="md:col-span-2">
                <LegalLabel required>Razão Social</LegalLabel>
                <LegalInput
                  value={safeQ.legal.companyName}
                  onChange={(v) =>
                    updateNestedField(
                      "qualification",
                      "legal",
                      "companyName",
                      v,
                    )
                  }
                  error={errors["qualification.legal.companyName"]}
                />
              </div>
            </div>
          </div>
        );
      case SAFE_CLIENT_TYPES.MINOR: {
        return (
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="md:col-span-4">
              <LegalLabel icon="fa-child" required>
                Nome do Menor / Assistido
              </LegalLabel>
              <LegalInput
                value={safeQ.minor.name}
                onChange={(v) =>
                  updateNestedField("qualification", "minor", "name", v)
                }
                error={errors["qualification.minor.name"]}
              />
            </div>
            <div className="md:col-span-2">
              <LegalLabel>CPF (se houver)</LegalLabel>
              <InputMasked
                mask="cpf"
                value={safeQ.minor.cpf}
                onChange={(v) =>
                  updateNestedField("qualification", "minor", "cpf", v)
                }
              />
            </div>
          </div>
        );
      }
      case SAFE_CLIENT_TYPES.ANIMAL: {
        return (
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="md:col-span-4">
              <LegalLabel icon="fa-paw" required>
                Nome do Animal
              </LegalLabel>
              <LegalInput
                value={safeQ.animal.name}
                onChange={(v) =>
                  updateNestedField("qualification", "animal", "name", v)
                }
                error={errors["qualification.animal.name"]}
              />
            </div>
            <div className="md:col-span-2">
              <LegalLabel>Espécie</LegalLabel>
              <LegalHybridInput
                listId="animal-species"
                options={SAFE_MASTER_DATA.ANIMAL_SPECIES}
                value={safeQ.animal.species}
                onChange={(e) =>
                  updateNestedField(
                    "qualification",
                    "animal",
                    "species",
                    e.target.value,
                  )
                }
              />
            </div>
            <div className="md:col-span-3">
              <LegalLabel>Raça</LegalLabel>
              <LegalHybridInput
                listId="animal-breed"
                options={SAFE_MASTER_DATA.ANIMAL_BREEDS}
                value={safeQ.animal.breed}
                onChange={(e) =>
                  updateNestedField(
                    "qualification",
                    "animal",
                    "breed",
                    e.target.value,
                  )
                }
              />
            </div>
            <div className="md:col-span-3">
              <LegalLabel>Classificação Científica</LegalLabel>
              <LegalInput
                value={safeQ.animal.scientificName || ""}
                onChange={(v) =>
                  updateNestedField(
                    "qualification",
                    "animal",
                    "scientificName",
                    v,
                  )
                }
                placeholder="Ex: Canis lupus familiaris"
              />
            </div>
            <div className="md:col-span-2">
              <LegalLabel>
                Sexo Biológico
                <InfoTooltip text="Animais não possuem identidade de gênero. Escolha o sexo cromossômico. 'Intersexo' aplica-se a anomalias genéticas raras." />
              </LegalLabel>
              <LegalSelect
                value={safeQ.animal.biologicalSex || ""}
                onChange={(e) =>
                  updateNestedField(
                    "qualification",
                    "animal",
                    "biologicalSex",
                    e.target.value,
                  )
                }
              >
                <option value="">Não informado</option>
                <option value="Macho">Macho</option>
                <option value="Fêmea">Fêmea</option>
                <option value="Intersexo">Intersexo</option>
              </LegalSelect>
            </div>
            <div className="md:col-span-2">
              <LegalLabel>
                Estado Reprodutivo
                <InfoTooltip text="Define o valor patrimonial de procriação. Animais 'Inteiros' possuem lucro cessante potencial. Castrados têm valor apenas afetivo." />
              </LegalLabel>
              <LegalHybridInput
                listId="animal-reproductive-status"
                options={SAFE_MASTER_DATA.ANIMAL_REPRODUCTIVE_STATUS}
                value={safeQ.animal.reproductiveStatus || ""}
                onChange={(e) =>
                  updateNestedField(
                    "qualification",
                    "animal",
                    "reproductiveStatus",
                    e.target.value,
                  )
                }
              />
            </div>
            <div className="md:col-span-2">
              <LegalLabel>RGA (se houver)</LegalLabel>
              <LegalInput
                value={safeQ.animal.rga || ""}
                onChange={(v) =>
                  updateNestedField("qualification", "animal", "rga", v)
                }
              />
            </div>
            <div className="md:col-span-2">
              <LegalLabel>Microchip (chipId)</LegalLabel>
              <LegalInput
                value={safeQ.animal.chipId || ""}
                onChange={(v) =>
                  updateNestedField("qualification", "animal", "chipId", v)
                }
              />
            </div>
            <div className="md:col-span-2">
              <LegalLabel>Microchip (campo técnico)</LegalLabel>
              <LegalInput
                value={safeQ.animal.microchip || ""}
                onChange={(v) =>
                  updateNestedField("qualification", "animal", "microchip", v)
                }
              />
            </div>
            <div className="md:col-span-2">
              <LegalLabel>Sexo</LegalLabel>
              <LegalSelect
                value={safeQ.animal.gender || ""}
                onChange={(e) =>
                  updateNestedField(
                    "qualification",
                    "animal",
                    "gender",
                    e.target.value,
                  )
                }
              >
                <option value="">Não informado</option>
                <option value="Macho">Macho</option>
                <option value="Fêmea">Fêmea</option>
              </LegalSelect>
            </div>
            <div className="md:col-span-2">
              <LegalLabel>
                Pelagem: Cor
                <InfoTooltip text="O pigmento base do animal (Ex: Preto, Azul/Cinza, Fawn)." />
              </LegalLabel>
              <LegalHybridInput
                listId="animal-coat-color"
                options={SAFE_MASTER_DATA.ANIMAL_COAT_COLORS}
                value={safeQ.animal.coat?.color || ""}
                onChange={(e) => updateAnimalCoatField("color", e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <LegalLabel>
                Pelagem: Padronagem
                <InfoTooltip text="A distribuição genética das manchas. (Ex: Tabby é o padrão tigrado felino; Merle é o padrão marmoreado canino)." />
              </LegalLabel>
              <LegalHybridInput
                listId="animal-coat-pattern"
                options={SAFE_MASTER_DATA.ANIMAL_COAT_PATTERNS}
                value={safeQ.animal.coat?.pattern || ""}
                onChange={(e) =>
                  updateAnimalCoatField("pattern", e.target.value)
                }
              />
            </div>
            <div className="md:col-span-2">
              <LegalLabel>
                Pelagem: Textura
                <InfoTooltip text="A característica física do fio (Liso, Arame/Duro, Crespo)." />
              </LegalLabel>
              <LegalHybridInput
                listId="animal-coat-texture"
                options={SAFE_MASTER_DATA.ANIMAL_COAT_TEXTURES}
                value={safeQ.animal.coat?.texture || ""}
                onChange={(e) =>
                  updateAnimalCoatField("texture", e.target.value)
                }
              />
            </div>
            <div className="md:col-span-6">
              <LegalLabel>
                Sinais Distintivos
                <InfoTooltip text="Sinais permanentes essenciais para perícia visual (Ex: Cicatrizes, amputações, fendas auriculares)." />
              </LegalLabel>
              <LegalInput
                value={
                  Array.isArray(safeQ.animal.distinctiveMarks)
                    ? safeQ.animal.distinctiveMarks.join("; ")
                    : safeQ.animal.distinctiveMarks || ""
                }
                onChange={(v) => updateAnimalArrayField("distinctiveMarks", v)}
                placeholder="Separe por ponto e vírgula"
              />
            </div>
            <div className="md:col-span-6">
              <LegalLabel>Registros Complementares</LegalLabel>
              <LegalInput
                value={
                  Array.isArray(safeQ.animal.registries)
                    ? safeQ.animal.registries.join("; ")
                    : safeQ.animal.registries || ""
                }
                onChange={(v) => updateAnimalArrayField("registries", v)}
                placeholder="Separe por ponto e vírgula"
              />
            </div>
          </div>
        );
      }
      case SAFE_CLIENT_TYPES.UNBORN:
      case SAFE_CLIENT_TYPES.NASCITURO: {
        return (
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="md:col-span-6">
              <LegalLabel icon="fa-baby" required>
                Nome da Gestante Representante
              </LegalLabel>
              <LegalInput
                value={safeQ.unborn.mothersName || ""}
                onChange={(v) =>
                  updateNestedField("qualification", "unborn", "mothersName", v)
                }
                error={errors["qualification.unborn.mothersName"]}
              />
            </div>
            <div className="md:col-span-3">
              <LegalLabel>Data Aproximada da Concepção</LegalLabel>
              <LegalInput
                value={safeQ.unborn.conceptionDate || ""}
                onChange={(v) =>
                  updateNestedField(
                    "qualification",
                    "unborn",
                    "conceptionDate",
                    v,
                  )
                }
                placeholder="AAAA-MM-DD"
              />
            </div>
            <div className="md:col-span-3">
              <LegalLabel>Data Provável do Parto</LegalLabel>
              <LegalInput
                value={safeQ.unborn.expectedBirthDate || ""}
                onChange={(v) =>
                  updateNestedField(
                    "qualification",
                    "unborn",
                    "expectedBirthDate",
                    v,
                  )
                }
                placeholder="AAAA-MM-DD"
              />
            </div>
          </div>
        );
      }
      case SAFE_CLIENT_TYPES.BANKRUPTCY: {
        return (
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="md:col-span-3">
              <LegalLabel icon="fa-gavel" required>
                CNPJ
              </LegalLabel>
              <InputMasked
                mask="cnpj"
                value={safeQ.bankruptcy.cnpj || ""}
                onChange={(v) =>
                  updateNestedField("qualification", "bankruptcy", "cnpj", v)
                }
                error={errors["qualification.bankruptcy.cnpj"]}
              />
            </div>
            <div className="md:col-span-3">
              <LegalLabel required>Razão Social (Massa Falida)</LegalLabel>
              <LegalInput
                value={safeQ.bankruptcy.companyName || ""}
                onChange={(v) =>
                  updateNestedField(
                    "qualification",
                    "bankruptcy",
                    "companyName",
                    v,
                  )
                }
                error={errors["qualification.bankruptcy.companyName"]}
              />
            </div>
          </div>
        );
      }
      default:
        return null;
    }
  };

  const renderQualificationFields = () => {
    const type = formData.type;
    const q = formData.qualification || {};
    const safeQ = {
      physical: q.physical || {},
      legal: q.legal || {},
      minor: q.minor || {},
      animal: q.animal || {},
    };

    if (type === SAFE_CLIENT_TYPES.PHYSICAL) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="md:col-span-1">
            <LegalLabel>Data de Nascimento</LegalLabel>
            <LegalInput
              type="date"
              value={safeQ.physical.birthDate || ""}
              onChange={(v) =>
                updateNestedField("qualification", "physical", "birthDate", v)
              }
            />
          </div>
          <div className="md:col-span-3">
            <LegalLabel>Nome da Mãe</LegalLabel>
            <LegalInput
              value={safeQ.physical.motherName || ""}
              onChange={(v) =>
                updateNestedField("qualification", "physical", "motherName", v)
              }
            />
          </div>
          <div className="md:col-span-2">
            <LegalLabel>Nome do Pai</LegalLabel>
            <LegalInput
              value={safeQ.physical.fatherName || ""}
              onChange={(v) =>
                updateNestedField("qualification", "physical", "fatherName", v)
              }
            />
          </div>
          <div className="md:col-span-3">
            <LegalLabel>Naturalidade (UF / Cidade)</LegalLabel>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <div className="md:col-span-1">
                <LegalSelect
                  value={naturalUF}
                  onChange={(e) => {
                    const uf = String(e.target.value || "").toUpperCase();
                    setNaturalUF(uf);
                    setNaturalCity("");
                    if (!uf) {
                      updateNestedField(
                        "qualification",
                        "physical",
                        "naturalness",
                        "",
                      );
                    } else {
                      updateNestedField(
                        "qualification",
                        "physical",
                        "naturalness",
                        `/${uf}`,
                      );
                    }
                  }}
                >
                  <option value="">UF</option>
                  {SAFE_MASTER_DATA.BRAZIL_STATES.map((uf) => (
                    <option key={uf} value={uf}>
                      {uf}
                    </option>
                  ))}
                </LegalSelect>
              </div>
              <div className="md:col-span-3">
                <LegalSelect
                  value={naturalCity}
                  onChange={(e) => {
                    const city = String(e.target.value || "");
                    setNaturalCity(city);
                    const uf = String(naturalUF || "")
                      .trim()
                      .toUpperCase();
                    updateNestedField(
                      "qualification",
                      "physical",
                      "naturalness",
                      city && uf ? `${city}/${uf}` : city,
                    );
                  }}
                  disabled={!naturalUF || citiesLoading}
                >
                  <option value="">
                    {citiesLoading ? "Carregando..." : "Cidade"}
                  </option>
                  {(Array.isArray(citiesCache?.[naturalUF])
                    ? citiesCache[naturalUF]
                    : []
                  ).map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </LegalSelect>
              </div>
            </div>
          </div>
          <div className="md:col-span-2">
            <LegalLabel>Nacionalidade</LegalLabel>
            <LegalHybridInput
              listId="nat-phys"
              options={SAFE_MASTER_DATA.NATIONALITIES}
              value={safeQ.physical.nationality}
              onChange={(e) =>
                updateNestedField(
                  "qualification",
                  "physical",
                  "nationality",
                  e.target.value,
                )
              }
            />
          </div>
          <div className="md:col-span-1">
            <LegalLabel>Estado Civil</LegalLabel>
            <LegalHybridInput
              listId="civ-phys"
              options={SAFE_MASTER_DATA.CIVIL_STATUS}
              value={safeQ.physical.civilStatus}
              onChange={(e) =>
                updateNestedField(
                  "qualification",
                  "physical",
                  "civilStatus",
                  e.target.value,
                )
              }
            />
          </div>
          {["Casado(a)", "Em União Estável"].includes(
            safeQ.physical.civilStatus,
          ) && (
            <div className="md:col-span-6 grid grid-cols-1 md:grid-cols-12 gap-4 p-4 mt-2 bg-blue-50/50 rounded-xl border border-blue-300 relative">
              <div className="absolute -top-3 left-4 bg-blue-100 text-blue-700 text-[9px] font-bold px-2 py-0.5 rounded uppercase tracking-wider">
                {["Casado(a)", "Em União Estável"].includes(
                  safeQ.physical.civilStatus,
                )
                  ? safeQ.physical.civilStatus === "Em União Estável"
                    ? "Dados do(a) Companheiro(a)"
                    : "Dados do Cônjuge"
                  : "Dados do Cônjuge"}
              </div>

              <div className="md:col-span-4">
                <LegalLabel required icon="fa-ring">
                  Regime de Bens
                </LegalLabel>
                <LegalSelect
                  value={safeQ.physical.maritalRegime || ""}
                  onChange={(e) =>
                    updateNestedField(
                      "qualification",
                      "physical",
                      "maritalRegime",
                      e.target.value,
                    )
                  }
                >
                  <option value="">Selecione o Regime...</option>
                  {SAFE_MASTER_DATA.MARITAL_REGIMES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </LegalSelect>
              </div>

              <div className="md:col-span-5">
                <LegalLabel required>
                  {safeQ.physical.civilStatus === "Em União Estável"
                    ? "Nome Completo do(a) Companheiro(a)"
                    : "Nome Completo do Cônjuge"}
                </LegalLabel>
                <div className="flex gap-2">
                  <LegalInput
                    value={safeQ.physical.spouseName || ""}
                    onChange={(v) =>
                      updateNestedField(
                        "qualification",
                        "physical",
                        "spouseName",
                        v,
                      )
                    }
                    placeholder={
                      safeQ.physical.civilStatus === "Em União Estável"
                        ? "Nome do(a) companheiro(a)"
                        : "Nome do marido/esposa"
                    }
                  />
                  <button
                    type="button"
                    onClick={() => setSpousePickerOpen(true)}
                    className="px-3 py-2 rounded-lg bg-white hover:bg-blue-50 text-[10px] font-bold text-blue-700 uppercase border border-blue-300"
                    title="Buscar Cônjuge no CRM (Banco de Clientes)"
                  >
                    <i className="fas fa-search"></i>
                  </button>
                </div>
              </div>

              <div className="md:col-span-3">
                <LegalLabel>
                  {safeQ.physical.civilStatus === "Em União Estável"
                    ? "CPF do(a) Companheiro(a)"
                    : "CPF do Cônjuge"}
                </LegalLabel>
                <InputMasked
                  mask="cpf"
                  value={safeQ.physical.spouseCPF || ""}
                  onChange={(v) =>
                    updateNestedField(
                      "qualification",
                      "physical",
                      "spouseCPF",
                      v,
                    )
                  }
                  placeholder="000.000.000-00"
                />
              </div>
            </div>
          )}
          <div className="md:col-span-6">
            <LegalLabel>Profissão</LegalLabel>
            <LegalHybridInput
              listId="prof-phys"
              options={SAFE_MASTER_DATA.PROFESSIONS}
              value={safeQ.physical.profession}
              onChange={(e) =>
                updateNestedField(
                  "qualification",
                  "physical",
                  "profession",
                  e.target.value,
                )
              }
            />
          </div>
          <div className="md:col-span-6 mt-2 bg-gray-50 p-4 rounded-xl border border-slate-300 shadow-md">
            <div className="flex items-center gap-2 mb-3">
              <input
                type="checkbox"
                checked={safeQ.physical.hasProfessionalRegister || false}
                onChange={(e) =>
                  updateNestedField(
                    "qualification",
                    "physical",
                    "hasProfessionalRegister",
                    e.target.checked,
                  )
                }
                className="w-4 h-4 text-brand-blue rounded"
              />
              <span className="text-xs font-bold text-gray-700 uppercase">
                Possui Registro de Classe (OAB, CRM, CREA...)?
              </span>
            </div>
            {safeQ.physical.hasProfessionalRegister ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <LegalLabel>Conselho / Tipo</LegalLabel>
                  <LegalHybridInput
                    listId="prof-council"
                    options={SAFE_MASTER_DATA.PROFESSIONAL_COUNCILS}
                    value={safeQ.physical.registerType}
                    onChange={(e) =>
                      updateNestedField(
                        "qualification",
                        "physical",
                        "registerType",
                        e.target.value,
                      )
                    }
                    placeholder="Selecione ou digite (Ex: OAB)"
                  />
                </div>
                <div>
                  <LegalLabel>UF do Registro</LegalLabel>
                  <LegalSelect
                    value={safeQ.physical.registerUf || ""}
                    onChange={(e) =>
                      updateNestedField(
                        "qualification",
                        "physical",
                        "registerUf",
                        e.target.value,
                      )
                    }
                  >
                    <option value="">Não informado</option>
                    {SAFE_MASTER_DATA.BRAZIL_STATES.map((uf) => (
                      <option key={uf} value={uf}>
                        {uf}
                      </option>
                    ))}
                  </LegalSelect>
                </div>
                <div>
                  <LegalLabel>Número do Registro</LegalLabel>
                  <div className="flex gap-2">
                    <LegalInput
                      value={safeQ.physical.professionalRegister || ""}
                      onChange={(v) =>
                        updateNestedField(
                          "qualification",
                          "physical",
                          "professionalRegister",
                          v,
                        )
                      }
                    />
                    <button
                      type="button"
                      onClick={handleVerifyOAB}
                      disabled={oabLoading}
                      className="px-3 py-2 rounded-lg bg-brand-blue text-white text-[10px] font-bold uppercase tracking-wider whitespace-nowrap disabled:opacity-60 hover:bg-brand-blue-darker active:scale-95"
                      title="Consultar OAB na base nacional"
                    >
                      {oabLoading ? (
                        <i className="fas fa-spinner fa-spin" />
                      ) : (
                        <i className="fas fa-search" />
                      )}
                      OAB
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      );
    }

    if (type === SAFE_CLIENT_TYPES.MINOR) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="md:col-span-2">
            <LegalLabel>Nacionalidade</LegalLabel>
            <LegalHybridInput
              listId="nat-minor"
              options={SAFE_MASTER_DATA.NATIONALITIES}
              value={safeQ.minor.nationality || "brasileiro(a)"}
              onChange={(e) =>
                updateNestedField(
                  "qualification",
                  "minor",
                  "nationality",
                  e.target.value,
                )
              }
            />
          </div>
          <div className="md:col-span-2">
            <LegalLabel>RG</LegalLabel>
            <LegalInput
              value={safeQ.minor.rg || ""}
              onChange={(v) =>
                updateNestedField("qualification", "minor", "rg", v)
              }
            />
          </div>
        </div>
      );
    }

    if (type === SAFE_CLIENT_TYPES.LEGAL) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="md:col-span-3">
            <LegalLabel>Nome Fantasia</LegalLabel>
            <LegalInput
              value={safeQ.legal.tradeName || ""}
              onChange={(v) =>
                updateNestedField("qualification", "legal", "tradeName", v)
              }
            />
          </div>
          <div className="md:col-span-3">
            <LegalLabel>Inscrição Estadual</LegalLabel>
            <LegalInput
              value={safeQ.legal.stateRegistry || ""}
              onChange={(v) =>
                updateNestedField("qualification", "legal", "stateRegistry", v)
              }
            />
          </div>
        </div>
      );
    }

    if (type === SAFE_CLIENT_TYPES.ANIMAL) return null;
    return null;
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-6 max-w-4xl mx-auto pb-10 h-[calc(100vh-140px)] overflow-y-auto"
    >
      <div className="grid grid-cols-1 gap-6">
        <div className="rounded-xl shadow-md border border-slate-300 overflow-hidden">
          <div className="bg-[#20243B] px-4 md:px-6 py-4 flex items-start justify-between gap-6">
            <div className="min-w-0">
              <h2 className="text-lg md:text-xl font-extrabold text-white uppercase tracking-widest flex items-center gap-2">
                <i className="fas fa-edit"></i>{" "}
                {initialData ? "Editando Cliente" : "Novo Cadastro"}
                {hasAnyPriority ? (
                  <span className="ml-2 px-2 py-1 rounded-md bg-amber-200 text-amber-900 text-[10px] font-extrabold uppercase tracking-wide">
                    Tramitação Prioritária
                  </span>
                ) : null}
              </h2>
              <div className="text-xs text-slate-200 mt-1">
                Cadastro estruturado em seções (modo workstation).
              </div>
            </div>
            <div className="w-56 flex-shrink-0">
              <label className="block text-[10px] font-bold text-slate-300 uppercase mb-1">
                Natureza Jurídica
              </label>
              <LegalSelect
                value={formData.type}
                onChange={(e) => {
                  const nextType = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    type: nextType,
                    healthCondition:
                      nextType === SAFE_CLIENT_TYPES.PHYSICAL ||
                      nextType === SAFE_CLIENT_TYPES.MINOR ||
                      nextType === SAFE_CLIENT_TYPES.IMPRISONED ||
                      nextType === SAFE_CLIENT_TYPES.UNBORN ||
                      nextType === SAFE_CLIENT_TYPES.NASCITURO
                        ? normalizeHealthCondition(prev?.healthCondition)
                        : [],
                    anchor: null,
                    livesWithAnchor: false,
                    __tutorLocked: false,
                  }));
                  setAnchorClient(null);
                  setInheritFromAnchor(false);
                  setAnchorSearch("");
                  setAnchorResults([]);
                  setAnchorPickerOpen(false);
                  setErrors((prev) => {
                    const n = { ...(prev || {}) };
                    delete n["anchor.id"];
                    return n;
                  });
                }}
              >
                {sortedClientTypes.map(([key, label]) => (
                  <option key={key} value={label}>
                    {label}
                  </option>
                ))}
              </LegalSelect>
            </div>
          </div>
        </div>

        {conflictWarning?.hasConflict ? (
          <div
            className={`border-l-4 p-4 rounded-r-xl shadow-sm ${conflictWarning.status === "danger" ? "bg-red-50 border-red-500 text-red-900" : "bg-amber-50 border-amber-500 text-amber-900"}`}
          >
            <div className="flex items-start gap-3">
              <div className="text-lg leading-none">
                {conflictWarning.status === "danger" ? "🚨" : "⚠️"}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-extrabold uppercase tracking-widest">
                  🚨 IMPEDIMENTO ÉTICO DETECTADO (COMPLIANCE OAB)
                </div>
                <div className="text-xs font-bold mt-1">
                  O sistema identificou que {conflictProbeName || "este perfil"}{" "}
                  já figura no banco de dados corporativo na condição de{" "}
                  {conflictWarning.type || "REGISTRO SENSÍVEL"}.
                </div>
                <div className="text-xs mt-2 whitespace-pre-wrap break-words">
                  A representação ou cadastro deste perfil exige auditoria de
                  compliance imediata para prevenção de Conflito de Interesses.
                </div>
                <div className="text-xs mt-2 whitespace-pre-wrap break-words font-medium">
                  {conflictWarning.message}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {conflictLoading ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-[11px] font-bold text-slate-500">
            Verificando conflito de interesses...
          </div>
        ) : null}

        <ConfigSection
          sectionId="IDENTIDADE"
          title="Identidade"
          subtitle="Nome e documentos"
          icon="fas fa-id-card"
          openSection={openSection}
          setOpenSection={setOpenSection}
        >
          <div className="p-3 rounded-xl border-2 border-slate-300 shadow-sm bg-white">
            <div className="text-[10px] font-bold uppercase text-gray-400 tracking-widest mb-3">
              Documentos
            </div>
            {renderTypeSpecificFields()}
          </div>
        </ConfigSection>

        <ConfigSection
          sectionId="VINCULO"
          title="Vínculo de Responsabilidade"
          subtitle="Tutor/âncora e regras de dependência"
          icon="fas fa-link"
          openSection={openSection}
          setOpenSection={setOpenSection}
        >
          <div className="bg-slate-50 p-4 md:p-5 rounded-xl border border-blue-300 border-l-4 border-l-blue-500">
            {!needsAnchor ? (
              <div className="text-sm font-bold text-gray-700">
                Auto-representado
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-xs text-gray-600">
                      Este bloco conecta o cadastro a um responsável (Pessoa
                      Física).
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setAnchorPickerOpen(true)}
                      className="px-3 py-2 rounded-lg bg-white hover:bg-blue-50 text-[10px] font-bold text-blue-700 uppercase border border-blue-300"
                      title="Buscar responsável no CRM (Banco de Clientes)"
                    >
                      CRM
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowQuickAddAnchor(true)}
                      className="px-3 py-2 rounded-lg bg-white hover:bg-blue-50 text-[10px] font-bold text-blue-700 uppercase border border-blue-300"
                    >
                      Novo
                    </button>
                  </div>
                </div>

                <div className="mt-4">
                  <LegalLabel icon="fa-user-shield" required>
                    {getAnchorLabelByType(formData?.type, age)}
                  </LegalLabel>
                  <LegalInput
                    value={anchorSearch}
                    onChange={(v) => setAnchorSearch(v)}
                    placeholder="Buscar por nome/CPF (mín. 2 letras)"
                    error={errors["anchor.id"]}
                  />
                  {anchorSearching ? (
                    <div className="text-[10px] text-gray-400 mt-1">
                      Buscando...
                    </div>
                  ) : null}
                  {anchorResults.length > 0 ? (
                    <div className="mt-2 border border-blue-300 rounded-lg overflow-hidden bg-white shadow-md">
                      {anchorResults.map((c) => {
                        const name =
                          c?.qualification?.physical?.name || "Pessoa Física";
                        const cpf = c?.qualification?.physical?.cpf || "";

                        return (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => handleSelectAnchor(c)}
                            className="w-full text-left p-2 hover:bg-blue-50 text-xs flex items-center justify-between"
                          >
                            <span className="font-bold text-gray-700 truncate">
                              {name}
                            </span>
                            <span className="font-mono text-[10px] text-gray-400">
                              {cpf}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </div>

                <div className="mt-4">
                  <LegalLabel>Vínculo</LegalLabel>
                  <LegalSelect
                    value={
                      formData.anchor?.role ||
                      getDefaultAnchorRoleForType(formData?.type)
                    }
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        anchor: prev?.anchor
                          ? { ...prev.anchor, role: e.target.value }
                          : { id: "", name: "", cpf: "", role: e.target.value },
                      }))
                    }
                  >
                    {Object.values(ANCHOR_ROLES).map((r) => (
                      <option key={r} value={r}>
                        {ANCHOR_ROLE_LABELS[r] || r}
                      </option>
                    ))}
                  </LegalSelect>
                </div>

                {formData.anchor?.id ? (
                  <div className="mt-4 p-3 rounded-lg bg-white border border-blue-300 shadow-md">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-[10px] font-bold uppercase text-blue-700">
                          Selecionado
                        </div>
                        <div className="mt-1 text-sm font-bold text-gray-800">
                          {formData.anchor?.name || "Responsável"}
                        </div>
                        <div className="text-[10px] text-gray-400 font-mono">
                          CPF: {formData.anchor?.cpf || "-"}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setAnchorPickerOpen(true)}
                          className="px-3 py-2 rounded-lg bg-white hover:bg-blue-50 text-[10px] font-bold text-blue-700 uppercase border border-blue-300"
                        >
                          Trocar
                        </button>
                        <button
                          type="button"
                          onClick={handleClearAnchor}
                          className="px-3 py-2 rounded-lg bg-white hover:bg-red-50 text-[10px] font-bold text-red-700 uppercase border border-red-200"
                        >
                          Remover
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}

                {showQuickAddAnchor ? (
                  <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
                    <div
                      className="absolute inset-0 bg-black/40"
                      onClick={() => setShowQuickAddAnchor(false)}
                    />
                    <div className="relative w-full max-w-md bg-white rounded-xl shadow-xl border border-slate-300 p-5">
                      <div className="text-sm font-bold text-[#20243B]">
                        Novo Responsável (Pessoa Física)
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Cadastro rápido para ancoragem.
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                        <div>
                          <LegalLabel required>Nome</LegalLabel>
                          <LegalInput
                            value={quickAnchorName}
                            onChange={(v) => setQuickAnchorName(v)}
                          />
                        </div>
                        <div>
                          <LegalLabel required>CPF</LegalLabel>
                          <InputMasked
                            mask="cpf"
                            value={quickAnchorCpf}
                            onChange={(v) => setQuickAnchorCpf(v)}
                          />
                        </div>
                      </div>

                      <div className="flex justify-end gap-2 mt-5">
                        <button
                          type="button"
                          onClick={() => setShowQuickAddAnchor(false)}
                          className="px-4 py-2 text-xs font-bold text-gray-700 bg-white border border-gray-300 shadow-sm hover:bg-gray-50 hover:shadow-md rounded-lg transition-transform active:scale-95"
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          disabled={creatingQuickAnchor}
                          onClick={handleCreateQuickAnchor}
                          className="px-4 py-2 text-xs font-bold text-white bg-[#20243B] border border-[#20243B] shadow-md hover:bg-[#1a1e30] hover:shadow-lg rounded-lg transition-transform active:scale-95"
                        >
                          {creatingQuickAnchor
                            ? "Criando..."
                            : "Criar e Vincular"}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </ConfigSection>

        {Boolean(formData?.id) &&
          formData?.type === SAFE_CLIENT_TYPES.PHYSICAL && (
            <ConfigSection
              sectionId="REPRESENTADOS"
              title="Representados / Tutela"
              subtitle="Vinculados a este responsável"
              icon="fas fa-users"
              openSection={openSection}
              setOpenSection={setOpenSection}
              rightSlot={
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCreateDependent && onCreateDependent(formData);
                  }}
                  className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-[10px] font-bold text-gray-700 uppercase"
                >
                  Adicionar
                </button>
              }
            >
              <div>
                {representedLoading ? (
                  <div className="text-xs text-gray-400">Carregando...</div>
                ) : represented.length === 0 ? (
                  <div className="text-xs text-gray-400">
                    Nenhum representado encontrado.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {represented.map((item) => {
                      const type = String(item?.type || "");
                      const name =
                        type === SAFE_CLIENT_TYPES.ANIMAL
                          ? item?.qualification?.animal?.name
                          : type === SAFE_CLIENT_TYPES.MINOR
                            ? item?.qualification?.minor?.name
                            : item?.qualification?.physical?.name ||
                              item?.qualification?.legal?.companyName ||
                              item?.qualificationText ||
                              "Representado";
                      const icon =
                        type === SAFE_CLIENT_TYPES.ANIMAL
                          ? "fa-paw"
                          : type === SAFE_CLIENT_TYPES.MINOR
                            ? "fa-baby"
                            : "fa-user";

                      return (
                        <div
                          key={item.id}
                          className="border border-slate-300 rounded-xl p-4 bg-white shadow-md hover:border-highlight-action"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <i
                                  className={`fas ${icon} text-highlight-action`}
                                ></i>

                                <div
                                  className="font-bold text-gray-800 text-sm truncate"
                                  title={name}
                                >
                                  {name}
                                </div>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => onOpenClient && onOpenClient(item)}
                              className="px-3 py-2 text-xs font-bold rounded-lg bg-brand-blue text-white uppercase flex-shrink-0"
                            >
                              Ver Ficha
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </ConfigSection>
          )}

        {showHealthSection ? (
          <ConfigSection
            sectionId="SAUDE"
            title="Condições de Saúde"
            subtitle="Prioridade etária e condições especiais"
            icon="fas fa-heartbeat"
            openSection={openSection}
            setOpenSection={setOpenSection}
          >
            <div className="space-y-3 mb-4">
              <div className="p-3 rounded-xl border-2 border-slate-300 shadow-sm bg-white">
                <div className="text-[10px] font-bold uppercase text-gray-400 tracking-widest mb-2">
                  Prioridade Etária
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {hasSuperAgedPriority ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border bg-[#20243B] text-white border-[#20243B]">
                      Super Idoso (80+) — Prioridade Máxima
                    </span>
                  ) : hasAgePriority ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border bg-amber-600 text-white border-amber-600">
                      Idoso (60+) — Prioridade Estatutária
                    </span>
                  ) : (
                    <span className="text-[10px] text-gray-400 italic">
                      Sem prioridade etária.
                    </span>
                  )}
                </div>
                <div className="mt-1.5 text-[9px] text-slate-400 italic">
                  Calculada automaticamente a partir da data de nascimento.
                </div>
              </div>
              <div className="p-3 rounded-xl border-2 border-slate-300 shadow-sm bg-white">
                <div className="text-[10px] font-bold uppercase text-gray-400 tracking-widest mb-2">
                  Condições de Saúde
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(SAFE_MASTER_DATA.HEALTH_CONDITIONS || [])
                    .filter(
                      (c) =>
                        c !== "NENHUMA" && c !== "IDOSO" && c !== "SUPER_IDOSO",
                    )
                    .map((c) => {
                      const selected = normalizeHealthCondition(
                        formData.healthCondition,
                      ).includes(c);
                      return (
                        <button
                          key={c}
                          type="button"
                          onClick={() => {
                            const current = normalizeHealthCondition(
                              formData.healthCondition,
                            );
                            const next = selected
                              ? current.filter((x) => x !== c)
                              : [...current, c];
                            updateRootField("healthCondition", next);
                          }}
                          className={`px-2.5 py-1 rounded-full text-[10px] font-bold border transition-colors ${
                            selected
                              ? "bg-[#20243B] text-white border-[#20243B]"
                              : "bg-white text-gray-500 border-slate-300 hover:border-slate-400"
                          }`}
                        >
                          {HEALTH_LABELS?.[c] || c}
                        </button>
                      );
                    })}
                </div>
                {normalizeHealthCondition(formData.healthCondition).length ===
                  0 && (
                  <div className="mt-1.5 text-[10px] text-gray-400 italic">
                    Nenhuma condição especial declarada.
                  </div>
                )}
              </div>
            </div>
          </ConfigSection>
        ) : null}

        <ConfigSection
          sectionId="QUALIFICACAO"
          title="Qualificação"
          subtitle="Dados cadastrais e vínculo"
          icon="fas fa-address-card"
          openSection={openSection}
          setOpenSection={setOpenSection}
        >
          {renderQualificationFields() ? (
            <div className="p-3 rounded-xl border-2 border-slate-300 shadow-sm bg-white">
              <div className="text-[10px] font-bold uppercase text-gray-400 tracking-widest mb-3">
                Qualificação
              </div>
              {renderQualificationFields()}
            </div>
          ) : null}

          <div className="mt-4 p-3 rounded-xl border-2 border-slate-300 shadow-sm bg-white">
            <LegalLabel icon="fa-gavel">
              Sociedade de Advogados
              <InfoTooltip text="Se cliente é uma sociedade de advogados, será necessário informar OAB ativa e certidão de quitação (Prov. 112/2006)." />
            </LegalLabel>
            <div className="flex items-center gap-2 flex-wrap mt-2">
              <label className="flex items-center gap-2 text-xs font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={Boolean(formData?.isLawFirm)}
                  onChange={(e) =>
                    updateRootField("isLawFirm", e.target.checked)
                  }
                  className="w-4 h-4 text-brand-blue rounded"
                />
                É Sociedade de Advogados
              </label>
              {oabResult ? (
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wide border ${
                    oabResult.found
                      ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                      : oabResult.error
                        ? "bg-red-50 text-red-700 border-red-300"
                        : "bg-amber-50 text-amber-700 border-amber-300"
                  }`}
                  title={
                    oabResult.found
                      ? `OAB: ${oabResult.data?.nome || oabResult.data?.name || ""}`
                      : String(oabResult.error || "Registro OAB inexistente")
                  }
                >
                  {oabLoading ? (
                    <i className="fas fa-spinner fa-spin text-[9px]" />
                  ) : oabResult.found ? (
                    <>
                      <i className="fas fa-check-circle text-[9px]" /> OAB
                      Válida
                    </>
                  ) : oabResult.error ? (
                    <>
                      <i className="fas fa-exclamation-circle text-[9px]" />{" "}
                      {oabResult.error}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-question-circle text-[9px]" />{" "}
                      Não encontrado
                    </>
                  )}
                </span>
              ) : oabLoading ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wide border bg-blue-50 text-blue-700 border-blue-300">
                  <i className="fas fa-spinner fa-spin text-[9px]" /> Consultando
                  OAB...
                </span>
              ) : null}
            </div>
          </div>

          {needsAnchor && formData?.anchor?.id && (
            <div className="mt-4 p-3 rounded-lg border border-blue-300 bg-slate-50">
              <label className="flex items-center gap-2 text-xs font-bold text-blue-900">
                <input
                  type="checkbox"
                  checked={Boolean(formData?.isFullDependent)}
                  onChange={(e) =>
                    updateRootField("isFullDependent", e.target.checked)
                  }
                />
                Dependência Total (Residência e Contato do Tutor)
              </label>
              {formData?.isFullDependent ? (
                <div className="mt-2 p-3 rounded-lg border border-blue-300 bg-white text-xs text-gray-700">
                  <div className="font-bold text-blue-800">
                    📍📞 Dados vinculados
                  </div>
                  <div className="mt-1">
                    Dados vinculados ao Tutor{" "}
                    <strong>{formData.anchor?.name || "Responsável"}</strong>.
                  </div>
                </div>
              ) : (
                <div className="text-[10px] text-gray-500 mt-1">
                  Se marcado, os dados serão puxados automaticamente do Tutor.
                </div>
              )}
            </div>
          )}
        </ConfigSection>

        <ConfigSection
          sectionId="CONTATO"
          title="Contato"
          subtitle="E-mail e telefone"
          icon="fas fa-phone"
          openSection={openSection}
          setOpenSection={setOpenSection}
        >
          {!formData?.isFullDependent ? (
            <div className="p-3 rounded-xl border-2 border-slate-300 shadow-sm bg-white">
              <div className="text-[10px] font-bold uppercase text-gray-400 tracking-widest mb-3">
                Contato
              </div>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                <div className="md:col-span-3">
                  <LegalLabel icon="fa-envelope" required>
                    E-mail
                  </LegalLabel>
                  <LegalInput
                    type="email"
                    value={formData?.contact?.email || ""}
                    onChange={(v) => updateField("contact", "email", v)}
                    onBlur={() =>
                      handleBlurValidation(
                        "contact.email",
                        formData?.contact?.email,
                        "EMAIL",
                      )
                    }
                    error={errors["contact.email"]}
                    placeholder="cliente@email.com"
                    disabled={inheritFromAnchor}
                  />
                </div>
                <div className="md:col-span-3">
                  <LegalLabel icon="fa-phone" required>
                    Telefone
                  </LegalLabel>
                  <InputMasked
                    mask="phone"
                    value={formData?.contact?.phonePrimary || ""}
                    onChange={(v) => updateField("contact", "phonePrimary", v)}
                    onBlur={() =>
                      handleBlurValidation(
                        "contact.phonePrimary",
                        formData?.contact?.phonePrimary,
                        "PHONE",
                      )
                    }
                    error={errors["contact.phonePrimary"]}
                    placeholder="(41) 99999-9999"
                    disabled={inheritFromAnchor}
                  />
                </div>
              </div>

              {/* --- CONTATO DE EMERGÊNCIA --- */}
              <div className="mt-4 pt-4 border-t border-slate-200">
                <div className="text-[10px] font-bold uppercase text-gray-400 tracking-widest mb-3">
                  Contato de Emergência
                </div>
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                  <div className="md:col-span-2">
                    <LegalLabel>Nome do Contato</LegalLabel>
                    <LegalInput
                      value={formData?.contact?.emergencyContact?.name || ""}
                      onChange={(v) =>
                        updateNestedField(
                          "contact",
                          "emergencyContact",
                          "name",
                          v,
                        )
                      }
                      placeholder="Nome completo"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <LegalLabel>Parentesco</LegalLabel>
                    <LegalInput
                      value={
                        formData?.contact?.emergencyContact?.relation || ""
                      }
                      onChange={(v) =>
                        updateNestedField(
                          "contact",
                          "emergencyContact",
                          "relation",
                          v,
                        )
                      }
                      placeholder="Ex: Mãe, Pai, Irmão"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <LegalLabel>Telefone</LegalLabel>
                    <InputMasked
                      mask="phone"
                      value={formData?.contact?.emergencyContact?.phone || ""}
                      onChange={(v) =>
                        updateNestedField(
                          "contact",
                          "emergencyContact",
                          "phone",
                          v,
                        )
                      }
                      placeholder="(41) 99999-9999"
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-3 rounded-xl border-2 border-slate-300 shadow-sm bg-white">
              <div className="text-[12px] text-gray-500 italic">
                Dados de contato vinculados ao Tutor.
              </div>
            </div>
          )}
        </ConfigSection>

        <ConfigSection
          sectionId="ENDERECO"
          title="Endereço"
          subtitle="Localização e CEP"
          icon="fas fa-map-marker-alt"
          openSection={openSection}
          setOpenSection={setOpenSection}
        >
          {formData?.isFullDependent ? (
            <div className="p-3 rounded-xl border-2 border-slate-300 shadow-sm bg-white">
              <div className="text-[12px] text-gray-500 italic">
                Endereço vinculado ao Tutor.
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              <div className="md:col-span-2">
                <LegalLabel icon="fa-map-marker-alt">CEP</LegalLabel>
                <div className="flex gap-2">
                  <InputMasked
                    mask="zip"
                    placeholder="00000-000"
                    value={formData.location.address.zipCode}
                    onChange={(v) =>
                      updateNestedField("location", "address", "zipCode", v)
                    }
                  />
                  <button
                    type="button"
                    onClick={handleCepSearch}
                    className="text-white bg-brand-blue px-3 rounded-lg"
                  >
                    <i
                      className={`fas ${enriching ? "fa-spinner fa-spin" : "fa-search"}`}
                    ></i>
                  </button>
                </div>
              </div>
              <div className="md:col-span-4">
                <LegalLabel>Logradouro (Rua/Av.)</LegalLabel>
                <LegalInput
                  placeholder="Ex: Av. República Argentina"
                  value={formData.location.address.street}
                  onChange={(v) =>
                    updateNestedField("location", "address", "street", v)
                  }
                  disabled={inheritFromAnchor}
                />
              </div>

              <div className="md:col-span-1">
                <LegalLabel>Número</LegalLabel>
                <LegalInput
                  placeholder="123"
                  value={formData.location.address.number}
                  onChange={(v) =>
                    updateNestedField("location", "address", "number", v)
                  }
                  disabled={inheritFromAnchor}
                />
              </div>
              <div className="md:col-span-2">
                <LegalLabel>Complemento</LegalLabel>
                <LegalInput
                  placeholder="Apto 101, Bloco B"
                  value={formData.location.address.complement}
                  onChange={(v) =>
                    updateNestedField("location", "address", "complement", v)
                  }
                  disabled={inheritFromAnchor}
                />
              </div>
              <div className="md:col-span-3">
                <LegalLabel>Bairro</LegalLabel>
                <LegalInput
                  placeholder="Ex: Água Verde"
                  value={formData.location.address.neighborhood}
                  onChange={(v) =>
                    updateNestedField("location", "address", "neighborhood", v)
                  }
                  disabled={inheritFromAnchor}
                />
              </div>

              <div className="md:col-span-6 grid grid-cols-4 gap-4">
                <div className="col-span-1">
                  <LegalLabel
                    required={
                      formData?.type === SAFE_CLIENT_TYPES.PHYSICAL ||
                      formData?.type === SAFE_CLIENT_TYPES.LEGAL
                    }
                  >
                    Estado (UF)
                  </LegalLabel>
                  <LegalSelect
                    value={formData.location.address.state}
                    onChange={(e) =>
                      updateNestedField(
                        "location",
                        "address",
                        "state",
                        e.target.value,
                      )
                    }
                    disabled={inheritFromAnchor}
                    error={errors?.["location.address.state"]}
                  >
                    {SAFE_MASTER_DATA.BRAZIL_STATES.map((uf) => (
                      <option key={uf} value={uf}>
                        {uf}
                      </option>
                    ))}
                  </LegalSelect>
                </div>
                <div className="col-span-3">
                  <LegalLabel
                    required={
                      formData?.type === SAFE_CLIENT_TYPES.PHYSICAL ||
                      formData?.type === SAFE_CLIENT_TYPES.LEGAL
                    }
                  >
                    Cidade
                  </LegalLabel>
                  <LegalInput
                    placeholder="Curitiba"
                    value={formData.location.address.city}
                    onChange={(v) =>
                      updateNestedField("location", "address", "city", v)
                    }
                    disabled={inheritFromAnchor}
                    error={errors?.["location.address.city"]}
                  />
                </div>
              </div>
            </div>
          )}
        </ConfigSection>

        <ConfigSection
          sectionId="OBSERVACOES"
          title="Observações"
          subtitle="Anotações internas do cadastro"
          icon="fas fa-sticky-note"
          openSection={openSection}
          setOpenSection={setOpenSection}
        >
          <div className="p-3 rounded-xl border-2 border-slate-300 shadow-sm bg-white">
            <div className="text-[10px] font-bold uppercase text-gray-400 tracking-widest mb-3">
              Observações Gerais
            </div>
            <textarea
              className="w-full min-h-[120px] px-3 py-2.5 text-sm border border-slate-200 rounded-lg outline-none placeholder-gray-300 bg-white hover:border-slate-300 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 resize-y"
              value={formData?.onboardingNotes || ""}
              onChange={(e) =>
                updateRootField("onboardingNotes", e.target.value)
              }
              placeholder="Informações complementares sobre o cliente, restrições, observações de atendimento..."
              maxLength={2000}
            />
            <div className="mt-1 text-[10px] text-gray-400 text-right">
              {String(formData?.onboardingNotes || "").length}/2000
            </div>
            <div className="mt-2 text-[10px] text-gray-500 italic">
              Essas observações são internas e não aparecem em documentos. Para
              histórico cronológico, use a aba Prontuário.
            </div>
          </div>
        </ConfigSection>

        {/* --- SEÇÃO TAGS DE SEGMENTAÇÃO (M6) --- */}
        <ConfigSection
          sectionId="TAGS"
          title="Tags de Segmentação"
          subtitle="Classificação complementar do cliente"
          icon="fas fa-tags"
          openSection={openSection}
          setOpenSection={setOpenSection}
        >
          <div className="p-3 rounded-xl border-2 border-slate-300 shadow-sm bg-white mb-4">
            <div className="text-[10px] font-bold uppercase text-gray-400 tracking-widest mb-2">
              Classificação do Contato (Selecione um ou mais)
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {["CLIENT", "PARTNER", "OTHER"].map((role) => (
                <button
                  key={role}
                  type="button"
                  onClick={() => {
                    const current = normalizeClientRoles(formData.roles);
                    const toggled = current.includes(role)
                      ? current.filter((r) => r !== role)
                      : [...current, role];
                    const newRoles = normalizeClientRoles(toggled);
                    setFormData({ ...formData, roles: newRoles });
                  }}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-bold border transition-colors ${
                    normalizeClientRoles(formData.roles).includes(role)
                      ? "bg-[#20243B] text-white border-[#20243B]"
                      : "bg-white text-gray-500 border-slate-300 hover:border-slate-400"
                  }`}
                >
                  {role === "CLIENT" && "Cliente"}
                  {role === "PARTNER" && "Advogado / Parceiro"}
                  {role === "OTHER" && "Outros"}
                </button>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-slate-200">
              <LegalLabel icon="fa-bullhorn" required>
                Origem do Cliente (Lead)
                <InfoTooltip text="Como o cliente chegou ao escritório. Obrigatório para triagem comercial." />
              </LegalLabel>
              <LegalSelect
                value={formData?.clientOrigin || ""}
                onChange={(e) =>
                  updateRootField("clientOrigin", e.target.value)
                }
                error={errors?.clientOrigin}
              >
                <option value="">Selecione a origem...</option>
                {(CLIENT_ORIGIN || []).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </LegalSelect>
            </div>
          </div>

          <div className="p-3 rounded-xl border-2 border-slate-300 shadow-sm bg-white">
            <div className="text-[10px] font-bold uppercase text-gray-400 tracking-widest mb-3">
              Tags
            </div>
            <div className="flex flex-wrap gap-2">
              {(CLIENT_TAGS || []).map((tag) => {
                const tags = Array.isArray(formData?.tags) ? formData.tags : [];
                const isActive = tags.includes(tag.value);
                const colorClass =
                  CLIENT_TAG_COLORS[tag.color] ||
                  "bg-slate-100 text-slate-700 border-slate-300";
                return (
                  <button
                    key={tag.value}
                    type="button"
                    onClick={() => {
                      const current = Array.isArray(formData?.tags)
                        ? [...formData.tags]
                        : [];
                      const next = isActive
                        ? current.filter((t) => t !== tag.value)
                        : [...current, tag.value];
                      updateRootField("tags", next);
                    }}
                    className={`px-3 py-1.5 rounded-full text-[10px] font-bold border transition-all ${
                      isActive
                        ? `${colorClass} ring-2 ring-offset-1 ring-brand-blue/30 scale-105`
                        : "bg-white text-slate-400 border-slate-200 hover:border-slate-300 hover:text-slate-600"
                    }`}
                  >
                    {tag.label}
                  </button>
                );
              })}
            </div>
            {Array.isArray(formData?.tags) && formData.tags.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <div className="text-[10px] font-bold uppercase text-gray-400 tracking-widest mb-2">
                  Tags ativas ({formData.tags.length})
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {formData.tags.map((tagVal) => {
                    const tagDef = (CLIENT_TAGS || []).find(
                      (t) => t.value === tagVal,
                    );
                    const colorClass = tagDef
                      ? CLIENT_TAG_COLORS[tagDef.color] ||
                        "bg-slate-100 text-slate-700"
                      : "bg-slate-100 text-slate-700";
                    return (
                      <span
                        key={tagVal}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider border ${colorClass}`}
                      >
                        {tagDef?.label || tagVal}
                        <button
                          type="button"
                          onClick={() => {
                            const current = Array.isArray(formData?.tags)
                              ? [...formData.tags]
                              : [];
                            updateRootField(
                              "tags",
                              current.filter((t) => t !== tagVal),
                            );
                          }}
                          className="ml-0.5 hover:text-red-600 transition-colors"
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </ConfigSection>

        {/* --- SEÇÃO LGPD / CONSENTIMENTO (A1) --- */}
        <ConfigSection
          sectionId="LGPD"
          title="LGPD / Consentimento"
          subtitle="Termo de consentimento para tratamento de dados"
          icon="fas fa-shield-alt"
          openSection={openSection}
          setOpenSection={setOpenSection}
        >
          <div className="p-3 rounded-xl border-2 border-slate-300 shadow-sm bg-white">
            <div className="flex flex-col gap-4">
              {/* Opt-in consentimento geral — APENAS VISUAL (cliente que consente) */}
              <label className="flex items-start gap-3 opacity-60 pointer-events-none select-none">
                <input
                  type="checkbox"
                  className="mt-0.5 w-4 h-4 rounded border-slate-300 text-brand-blue"
                  checked={formData?.lgpdConsent?.dataUsageConsent ?? false}
                  readOnly
                  tabIndex={-1}
                />
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-bold text-slate-500">
                    Autorizo o tratamento de dados pessoais
                  </span>
                  <span className="text-[10px] text-slate-400 leading-relaxed">
                    O cliente autoriza o escritório a tratar seus dados pessoais
                    para fins de execução dos serviços contratados, conforme a
                    LGPD (Lei 13.709/2018).
                  </span>
                </div>
              </label>

              {/* Opt-in dados sensíveis de saúde — APENAS VISUAL (cliente que consente) */}
              <label className="flex items-start gap-3 opacity-60 pointer-events-none select-none">
                <input
                  type="checkbox"
                  className="mt-0.5 w-4 h-4 rounded border-slate-300 text-brand-blue"
                  checked={formData?.lgpdConsent?.healthDataConsent ?? false}
                  readOnly
                  tabIndex={-1}
                />
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-bold text-slate-500">
                    Autorizo o tratamento de dados sensíveis de saúde
                  </span>
                  <span className="text-[10px] text-slate-400 leading-relaxed">
                    Consentimento específico para dados sensíveis (art. 11
                    LGPD), como condições de saúde, internações e deficiências.
                  </span>
                </div>
              </label>
            </div>
          </div>
        </ConfigSection>

        <SaveCancelBar
          onCancel={onCancel}
          cancelLabel="Cancelar"
          saveLabel="Salvar e Fechar"
          extraSaveButton={{ label: "Salvar", value: "keep-open" }}
          saving={loading}
          disabledSave={loading}
          saveButtonType="submit"
          className="pt-4"
        />

        {spousePickerOpen && (
          <CrmClientPickerModal
            isOpen={true}
            onClose={() => setSpousePickerOpen(false)}
            onSelect={handleSpouseSelect}
            db={db}
            appId={appId}
            title="Vincular Cônjuge (CRM)"
            requiredType="Pessoa Física"
          />
        )}
        {anchorPickerOpen && (
          <CrmClientPickerModal
            isOpen={true}
            onClose={() => setAnchorPickerOpen(false)}
            onSelect={handleSelectAnchor}
            db={db}
            appId={appId}
            title={`${getAnchorLabelByType(formData?.type, age)} (CRM)`}
            requiredType="Pessoa Física"
          />
        )}
      </div>

      {toastMsg ? (
        <div className="fixed bottom-6 right-6 z-[9999] bg-[#20243B] text-white text-xs font-bold px-4 py-2.5 rounded-xl shadow-2xl border border-[#C5A059]/30 flex items-center gap-2 animate-fade-in-up">
          <span>{toastMsg}</span>
        </div>
      ) : null}
    </form>
  );
};

// --- LISTAGEM DE CLIENTES ---
const ClientList = ({ onEdit, onCreate, onDuplicate, refreshSignal }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [creatingAccess, setCreatingAccess] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [confirmState, setConfirmState] = useState({
    open: false,
    message: "",
    resolve: null,
  });
  const [alertState, setAlertState] = useState({
    open: false,
    message: "",
    resolve: null,
  });
  const [importPayload, setImportPayload] = useState(null);

  const confirmAsync = (message) => {
    return new Promise((resolve) => {
      setConfirmState({ open: true, message: String(message || ""), resolve });
    });
  };

  const alertAsync = (message) => {
    return new Promise((resolve) => {
      setAlertState({ open: true, message: String(message || ""), resolve });
    });
  };

  const safeConfirm = async (message) => {
    return await confirmAsync(message);
  };

  const safeAlert = async (message) => {
    await alertAsync(message);
  };

  // 1. CARREGAMENTO BLINDADO (Server First)
  useEffect(() => {
    const fetchClients = async () => {
      try {
        setLoading(true);
        const data = await repo.fetchAllClients(appId);
        setClients(data);
      } catch (e) {
        console.error("Erro busca padrão:", e);
        setClients([]);
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, [refreshTrigger, refreshSignal]);

  // GERADOR DE SENHA FORTE (V21.0 - SECURITY ERA)
  const generateStrongPassword = () => {
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const nums = "0123456789";
    const special = "!@#$%&*";
    const all = lower + upper + nums + special;

    let password = "";
    password += upper[Math.floor(Math.random() * upper.length)];
    password += special[Math.floor(Math.random() * special.length)];
    password += nums[Math.floor(Math.random() * nums.length)];

    for (let i = 0; i < 9; i++) {
      password += all[Math.floor(Math.random() * all.length)];
    }

    return password
      .split("")
      .sort(() => 0.5 - Math.random())
      .join("");
  };

  const handleCreateAccess = async (client) => {
    console.log("Botão Acesso Clicado para ID:", client?.id);
    const email = client.contact?.email;
    let name = "Cliente";
    if (client.type === SAFE_CLIENT_TYPES.PHYSICAL)
      name = client.qualification?.physical?.name;
    else if (client.type === SAFE_CLIENT_TYPES.LEGAL)
      name = client.qualification?.legal?.companyName;

    if (!email) {
      await safeAlert("Este cliente não possui e-mail cadastrado.");
      return;
    }

    const ok = await safeConfirm(
      `Deseja enviar convite de acesso para ${name}?\n\nE-mail: ${email}\n\n(O cliente definirá a senha no link de ativação)`,
    );
    if (!ok) return;

    setCreatingAccess(client.id);

    const timeoutPromise = new Promise(
      (_, reject) =>
        setTimeout(
          () =>
            reject(
              new Error("Timeout: O servidor demorou muito para responder."),
            ),
          25000,
        ), // Aumentado para 25s
    );

    try {
      console.log("Iniciando envio de convite...");
      const createFunc = httpsCallable(functions, "inviteUser");

      const result = await Promise.race([
        createFunc({ email, name }),
        timeoutPromise,
      ]);

      const responseData = result?.data;

      console.log("📦 Payload Recebido:", result);

      if (responseData && responseData.success) {
        const emailStatus = responseData.emailSent
          ? "📧 CONVITE ENVIADO!"
          : `⚠️ FALHA NO ENVIO: ${responseData.debugError || "Erro desconhecido"}`;
        await safeAlert(
          `✅ CONVITE PROCESSADO\n${emailStatus}\n\nUSUÁRIO: ${email}`,
        );
        const phone = String(client?.contact?.phonePrimary || "").replace(
          /\D/g,
          "",
        );
        if (phone.length >= 10) {
          const msg = encodeURIComponent(
            `Olá ${name}! Seu acesso ao Portal RDA foi ativado. Faça login com o e-mail: ${email}`,
          );
          window.open(`https://wa.me/55${phone}?text=${msg}`, "_blank");
        }
      } else {
        const debugInfo = JSON.stringify(result || "NULO");
        const erroMsg =
          responseData?.error ||
          "Resposta vazia do servidor (Function retornou void).";
        console.error("Erro Backend Data:", result);
        await safeAlert(
          `❌ FALHA NO SERVIDOR\n\nMotivo: ${erroMsg}\n\nDebug Info: ${debugInfo}`,
        );
      }
    } catch (e) {
      console.error("Erro Crítico:", e);
      await safeAlert(
        `❌ ERRO DE CONEXÃO\n\nDetalhes: ${e.message || e}\n\nDica: Verifique se as Cloud Functions foram deployadas corretamente.`,
      );
    } finally {
      setCreatingAccess(null);
    }
  };

  const handleAdminResetMfa = async (client) => {
    const email = client?.contact?.email;
    if (!email) {
      await safeAlert("Este cliente não possui e-mail cadastrado.");
      return;
    }

    const ok1 = await safeConfirm(
      `🚨 RESETAR MFA\n\nIsso vai remover o 2FA (Authenticator) do cliente.\n\nCliente: ${email}\n\nConfirmar?`,
    );
    if (!ok1) return;
    const ok2 = await safeConfirm(
      `ÚLTIMA CONFIRMAÇÃO\n\nTem certeza absoluta que deseja remover o MFA de: ${email}?`,
    );
    if (!ok2) return;

    setCreatingAccess(client.id);
    try {
      const fn = httpsCallable(functions, "adminResetMFA");
      const res = await fn({ email });
      if (res?.data?.success) {
        await safeAlert("✅ MFA removido com sucesso.");
      } else {
        await safeAlert(
          `❌ Falha ao resetar MFA: ${res?.data?.error || "Erro desconhecido"}`,
        );
      }
    } catch (e) {
      await safeAlert(`❌ Erro ao resetar MFA: ${e?.message || e}`);
    } finally {
      setCreatingAccess(null);
    }
  };

  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importRawText, setImportRawText] = useState("");

  // --- IMPORTAÇÃO JSON (HITL - Schema Zod + ImportPreviewModal) ---
  const handleImportClick = () => {
    setImportRawText("");
    setImportModalOpen(true);
  };

  const handleImportConfirm = () => {
    const raw = importRawText;
    if (!raw || !raw.trim()) {
      safeAlert("Nenhum JSON foi colado.");
      return;
    }

    // Remove linhas de comentário (//), espaços extras e quebras de linha
    // para permitir colar JSON com comentários de documentação
    const cleaned = raw
      .split(/\r?\n/)
      .map((line) => {
        const trimmed = line.trim();
        // Remove linhas que são apenas comentários ou separadores
        if (
          trimmed.startsWith("//") ||
          trimmed.startsWith("#") ||
          trimmed.startsWith("--")
        )
          return "";
        return line;
      })
      .join("\n")
      .trim();

    try {
      const parsed = JSON.parse(cleaned);
      const extracted = parsed?.data || parsed;
      const validation = validateClientImport(extracted);

      if (!validation.valid) {
        safeAlert(
          "Erro de validação:\n" +
            (validation.errors || [])
              .map((e) => `  - ${e.path || ""}: ${e.message || ""}`)
              .join("\n"),
        );
        setImportModalOpen(false);
        setImportRawText("");
        return;
      }

      // Monta envelope completo com module + action para o ImportRepository
      const envelope = {
        schema_version: parsed?.schema_version || "1.0",
        module: "clientes",
        action: "create",
        data: extracted,
        metadata: parsed?.metadata || {
          agent: "portal-rda",
          operator: "admin",
          generated_at: new Date().toISOString(),
        },
      };
      setImportPayload(envelope);
      setImportModalOpen(false);
      setImportRawText("");
    } catch (e) {
      const msg = e.message || "";
      // Detecta se colaram código JSX/JS em vez de JSON
      if (
        msg.includes("Unexpected token") ||
        msg.includes("is not valid JSON")
      ) {
        safeAlert(
          "O texto colado não é um JSON válido.\n\n" +
            "Dica: você deve copiar o CONTEÚDO do arquivo tmp_modelo_importacao_lote.json\n" +
            "(não o código do ClientManager.jsx).\n\n" +
            "Abra o arquivo .json, selecione tudo (Ctrl+A), copie (Ctrl+C) e cole aqui.\n\n" +
            "Erro: " +
            msg,
        );
      } else {
        safeAlert("JSON inválido: " + msg);
      }
    }
  };

  // --- LIMPEZA NUCLEAR (SOLUÇÃO DA PESQUISA 3.2) ---
  const handleNuclearClean = async () => {
    const ok = await safeConfirm(
      "⚠️ MODO FORENSE: Isso irá varrer TODO o banco de dados em busca de QUALQUER documento 'clients' e apagar permanentemente.\n\nTem certeza?",
    );
    if (!ok) return;

    setLoading(true);
    try {
      console.log("Iniciando varredura nuclear via collectionGroup...");
      const result = await repo.nuclearDeleteAllClients();

      if (!result.deleted) {
        await safeAlert("Nenhum cliente encontrado em lugar nenhum do banco.");
        setClients([]);
        setLoading(false);
        return;
      }

      setClients([]);
      await safeAlert(
        `✅ Limpeza Nuclear Concluída. ${result.count} registros apagados.`,
      );
      setRefreshTrigger((prev) => prev + 1);
    } catch (e) {
      console.error("Erro Nuclear:", e);
      if (e.message.includes("requires an index")) {
        await safeAlert(
          "⚠️ O Firebase exige um índice para collectionGroup.\nAbra o console (F12) e clique no link fornecido pelo Google.",
        );
      } else {
        await safeAlert("Erro fatal na limpeza: " + e.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // DELETE INDIVIDUAL (COM DEBUG FORENSE E BLINDAGEM DE PATH V24.0)
  const handleDeleteSingle = async (client) => {
    // 1. Guarda de Integridade (ID deve existir)
    if (!client || !client.id) {
      await safeAlert(
        "ERRO CRÍTICO: Tentativa de excluir um registro fantasma (ID indefinido).\n\nRecarregue a página (Ctrl+F5).",
      );
      return;
    }

    const name =
      client.qualification?.physical?.name ||
      client.qualification?.legal?.companyName ||
      "este cliente";
    const ok = await safeConfirm(`Excluir ${name}?`);
    if (!ok) return;

    try {
      // CORREÇÃO CRÍTICA (V24.0): Construção segura de caminho (String Path)
      // Isso evita o erro 'Cannot read properties of null (reading 'indexOf')'
      // se o appId ou client.id forem objetos por algum motivo.
      const cleanId = String(client.id);
      const safePath = `artifacts/${appId}/public/data/clients/${cleanId}`;

      console.log(
        `[DELETE] Tentando deletar via caminho explícito: ${safePath}`,
      );

      await repo.softDeleteClient(appId, client.id);

      setClients((prev) => prev.filter((c) => c.id !== client.id));
    } catch (e) {
      console.error("Erro Delete:", e);
      await safeAlert(
        `Erro Fatal ao excluir:\n${e.message}\n\nTente recarregar a página.`,
      );
    }
  };

  const filteredClients = clients
    .filter((c) => {
      if (c.isDeleted === true) return false;
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      const name =
        c.qualification?.physical?.name ||
        c.qualification?.legal?.companyName ||
        "";
      const doc =
        c.qualification?.physical?.cpf || c.qualification?.legal?.cnpj || "";
      return name.toLowerCase().includes(term) || doc.includes(term);
    })
    .sort((a, b) => {
      const resolveSortName = (c) => {
        const t = String(c?.type || "").toUpperCase();
        if (t === "PESSOA FÍSICA" || t === "PHYSICAL")
          return (
            c?.qualification?.physical?.socialName ||
            c?.qualification?.physical?.name ||
            ""
          );
        if (t === "PESSOA JURÍDICA" || t === "PESSOA JURIDICA" || t === "LEGAL")
          return c?.qualification?.legal?.companyName || "";
        if (
          t === "ANIMAL" ||
          t === "ANIMAL NÃO-HUMANO" ||
          t === "ANIMAL NAO-HUMANO"
        )
          return c?.qualification?.animal?.name || "";
        if (t === "MENOR" || t === "MINOR")
          return c?.qualification?.minor?.name || "";
        if (t === "ESPÓLIO" || t === "ESPOLIO" || t === "ESTATE")
          return c?.qualification?.estate?.deceasedName || "";
        if (t === "CONDOMÍNIO" || t === "CONDOMINIO" || t === "CONDOMINIUM")
          return c?.qualification?.condominium?.name || "";
        if (t === "MASSA FALIDA" || t === "BANKRUPTCY")
          return c?.qualification?.bankruptcy?.companyName || "";
        if (t === "PRESO" || t === "IMPRISONED")
          return c?.qualification?.imprisoned?.name || "";
        return c?.name || c?.qualificationText || "";
      };
      const na = String(resolveSortName(a)).trim();
      const nb = String(resolveSortName(b)).trim();
      return na.localeCompare(nb, "pt-BR", { sensitivity: "base" });
    });

  return (
    <div>
      <div className="mb-6 rounded-xl shadow-md border border-slate-300 overflow-hidden">
        <div className="bg-[#20243B] p-4 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <h2 className="text-lg md:text-xl font-extrabold text-white uppercase tracking-widest">
              Base de Clientes
            </h2>
            <div className="text-xs text-slate-200 mt-1">
              Pesquisa rápida + gestão central.
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-3 md:items-center">
            <div className="relative">
              <input
                className="w-full md:w-72 pl-9 pr-4 py-2 rounded-lg text-xs outline-none border border-white/20 bg-white/10 text-white placeholder-slate-300 focus:bg-white/15 focus:border-white/40"
                placeholder="Nome, CPF ou CNPJ..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <i className="fas fa-search absolute left-3 top-2.5 text-slate-300 text-xs"></i>
            </div>

            <div className="flex gap-2">
              <button
                onClick={onCreate}
                className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-xs font-extrabold uppercase"
              >
                <i className="fas fa-plus"></i> Novo
              </button>

              <button
                onClick={handleImportClick}
                className="bg-[#C5A059] text-white px-3 py-2 rounded-lg text-xs font-extrabold uppercase"
                title="Importar cliente via JSON canônico"
              >
                <i className="fas fa-file-import"></i> Importar
              </button>

              <button
                onClick={handleNuclearClean}
                className="bg-red-600 text-white px-3 py-2 rounded-lg text-xs font-extrabold uppercase"
                title="Limpar Banco de Dados (Nuclear)"
              >
                <i className="fas fa-radiation"></i> Limpeza
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center p-10">
          <i className="fas fa-spinner fa-spin text-2xl text-brand-blue"></i>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md border border-slate-300 overflow-hidden">
          <div className="w-full">
            <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
              <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,2fr)] gap-3 p-4 items-center">
                <div className="text-[10px] font-bold text-gray-500 uppercase">
                  Nome / Contato
                </div>
                <div className="text-[10px] font-bold text-gray-500 uppercase">
                  Documento
                </div>
                <div className="text-[10px] font-bold text-gray-500 uppercase">
                  Tipo
                </div>
                <div className="text-[10px] font-bold text-gray-500 uppercase">
                  Perfis
                </div>
                <div className="text-[10px] font-bold text-gray-500 uppercase">
                  Ações
                </div>
              </div>
            </div>

            {filteredClients.length === 0 ? (
              <div className="p-10 text-center text-xs text-gray-400">
                Nenhum cliente encontrado.
              </div>
            ) : (
              <div className="divide-y divide-slate-200">
                {filteredClients.map((client) => {
                  const resolveName = (c) => {
                    const t = String(c?.type || "").toUpperCase();
                    if (t === "PESSOA FÍSICA" || t === "PHYSICAL")
                      return (
                        c?.qualification?.physical?.name ||
                        c?.name ||
                        "Pessoa Física"
                      );
                    if (
                      t === "PESSOA JURÍDICA" ||
                      t === "PESSOA JURIDICA" ||
                      t === "LEGAL"
                    )
                      return (
                        c?.qualification?.legal?.companyName ||
                        c?.name ||
                        "Pessoa Jurídica"
                      );
                    if (
                      t === "ANIMAL" ||
                      t === "ANIMAL NÃO-HUMANO" ||
                      t === "ANIMAL NAO-HUMANO"
                    )
                      return (
                        c?.qualification?.animal?.name || c?.name || "Animal"
                      );
                    if (t === "MENOR" || t === "MINOR")
                      return (
                        c?.qualification?.minor?.name || c?.name || "Menor"
                      );
                    if (t === "ESPÓLIO" || t === "ESPOLIO" || t === "ESTATE")
                      return (
                        c?.qualification?.estate?.deceasedName ||
                        c?.name ||
                        "Espólio"
                      );
                    if (
                      t === "CONDOMÍNIO" ||
                      t === "CONDOMINIO" ||
                      t === "CONDOMINIUM"
                    )
                      return (
                        c?.qualification?.condominium?.name ||
                        c?.name ||
                        "Condomínio"
                      );
                    return c?.name || "Registro";
                  };

                  const resolveDoc = (c) => {
                    return (
                      c?.cpf ||
                      c?.cnpj ||
                      c?.qualification?.physical?.cpf ||
                      c?.qualification?.legal?.cnpj ||
                      ""
                    );
                  };

                  const resolveContractClassification = (c) => {
                    const raw =
                      c?.contractClassification ??
                      c?.contractType ??
                      c?.classificacaoContrato ??
                      c?.classificacao_contrato ??
                      c?.contract_classification ??
                      c?.contract_type ??
                      "";
                    const s = String(raw || "").trim();
                    return s || "—";
                  };

                  const roles =
                    Array.isArray(client.roles) && client.roles.length > 0
                      ? client.roles
                      : ["CLIENT"];
                  const displayName = resolveName(client);
                  const docValue = resolveDoc(client);
                  const contractClassification =
                    resolveContractClassification(client);
                  const typeLabel =
                    String(client?.type || "").trim() || "Não informado";
                  const roleLabels = roles
                    .map((r) => getRoleLabel(r))
                    .filter(Boolean);

                  return (
                    <div
                      key={client.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => onEdit(client)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") onEdit(client);
                      }}
                      className="cursor-pointer hover:bg-slate-100"
                    >
                      <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,2fr)] gap-3 p-4 items-center">
                        <div className="min-w-0">
                          <div className="font-bold text-gray-800 text-sm truncate flex items-center gap-2">
                            {displayName}
                            {client.onboardingStatus === "PENDING_APPROVAL" && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-widest bg-amber-100 text-amber-700 border border-amber-300 flex-shrink-0">
                                Pendente
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] text-gray-400 mt-1 truncate">
                            {client.contact?.email || ""}
                          </div>
                        </div>
                        <div className="text-xs text-gray-700 font-mono break-words">
                          {docValue || "—"}
                        </div>
                        <div>
                          <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                            <i
                              className={`${getClientEntityIconClass(client?.type)} text-slate-400`}
                            />
                            <span>{typeLabel}</span>
                          </div>
                        </div>
                        <div>
                          <div
                            className="text-xs text-slate-700 truncate"
                            title={roleLabels.join(" • ")}
                          >
                            {roleLabels.join(" • ") || "Cliente"}
                          </div>
                        </div>
                        <div className="flex justify-start gap-2 flex-wrap">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleCreateAccess(client);
                            }}
                            disabled={creatingAccess === client.id}
                            className="cursor-pointer pointer-events-auto px-3 py-1.5 rounded-lg text-[11px] font-extrabold uppercase tracking-widest border border-[#C5A059] text-[#C5A059] bg-white hover:bg-amber-50 active:scale-95 flex items-center gap-2"
                            title="Enviar convite de acesso"
                          >
                            {creatingAccess === client.id ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                              <i className="fas fa-paper-plane"></i>
                            )}
                            CONVIDAR
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleAdminResetMfa(client);
                            }}
                            className="px-2.5 py-1.5 rounded-lg text-[11px] font-extrabold uppercase tracking-widest border border-red-200 text-red-700 bg-white hover:bg-red-50"
                            title="Resetar MFA"
                          >
                            <i className="fas fa-shield-alt"></i>
                          </button>
                          {client.onboardingStatus === "PENDING_APPROVAL" && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleApproveOnboarding(client);
                              }}
                              className="px-2.5 py-1.5 rounded-lg text-[11px] font-extrabold uppercase tracking-widest border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
                              title="Homologar cadastro"
                            >
                              <i className="fas fa-check-circle"></i> Homologar
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onDuplicate && onDuplicate(client);
                            }}
                            className="px-2.5 py-1.5 rounded-lg text-[11px] font-extrabold uppercase tracking-widest border border-slate-200 text-slate-500 bg-white hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200"
                            title="Duplicar cliente (Clonagem Inteligente)"
                          >
                            <Copy size={12} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDeleteSingle(client);
                            }}
                            className="px-2.5 py-1.5 rounded-lg text-[11px] font-extrabold uppercase tracking-widest border border-slate-200 text-slate-500 bg-white hover:bg-red-50 hover:text-red-700 hover:border-red-200"
                            title="Excluir (Soft Delete — LGPD)"
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Modal para colar JSON multi-linha */}
      {importModalOpen && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              setImportModalOpen(false);
              setImportRawText("");
            }}
          />
          <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-xl border border-slate-300 p-5">
            <div className="text-sm font-bold text-[#20243B] mb-1">
              Importar JSON
            </div>
            <div className="text-xs text-gray-500 mb-3">
              Cole abaixo o JSON canônico do cliente. Pode colar com várias
              linhas.
            </div>
            <textarea
              className="w-full h-64 px-3 py-2.5 text-xs font-mono border border-slate-200 rounded-lg outline-none resize-y focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20"
              placeholder='{"type": "Pessoa Física", "qualification": {"physical": {"name": "..."}}}'
              value={importRawText}
              onChange={(e) => setImportRawText(e.target.value)}
              spellCheck={false}
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setImportModalOpen(false);
                  setImportRawText("");
                }}
                className="px-4 py-2 text-xs font-bold text-gray-700 bg-white border border-gray-300 shadow-sm hover:bg-gray-50 rounded-lg"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleImportConfirm}
                className="px-4 py-2 text-xs font-bold text-white bg-[#C5A059] border border-[#C5A059] shadow-md hover:bg-[#b89450] rounded-lg"
              >
                Validar e Importar
              </button>
            </div>
          </div>
        </div>
      )}

      <ImportPreviewModal
        isOpen={!!importPayload}
        onClose={() => setImportPayload(null)}
        payload={importPayload}
        moduleName="clientes"
        validator={validateClientImport}
      />

      {confirmState.open ? (
        <div className="fixed inset-0 z-[500] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              const r = confirmState.resolve;
              setConfirmState({ open: false, message: "", resolve: null });
              if (typeof r === "function") r(false);
            }}
          />
          <div className="relative w-full max-w-md bg-white rounded-xl shadow-xl border border-slate-300 p-5">
            <div className="text-sm font-bold text-[#20243B]">
              Confirmar Ação
            </div>
            <div className="text-xs text-gray-600 mt-2 whitespace-pre-wrap">
              {confirmState.message}
            </div>
            <div className="mt-5 flex justify-start gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => {
                  const r = confirmState.resolve;
                  setConfirmState({ open: false, message: "", resolve: null });
                  if (typeof r === "function") r(false);
                }}
                className="px-4 py-2 text-xs font-bold text-gray-700 bg-white border border-gray-300 shadow-sm hover:bg-gray-50 hover:shadow-md rounded-lg transition-transform active:scale-95"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  const r = confirmState.resolve;
                  setConfirmState({ open: false, message: "", resolve: null });
                  if (typeof r === "function") r(true);
                }}
                className="px-4 py-2 text-xs font-bold text-white bg-[#20243B] border border-[#20243B] shadow-md hover:bg-[#1a1e30] hover:shadow-lg rounded-lg transition-transform active:scale-95"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {alertState.open ? (
        <div className="fixed inset-0 z-[500] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              const r = alertState.resolve;
              setAlertState({ open: false, message: "", resolve: null });
              if (typeof r === "function") r(true);
            }}
          />
          <div className="relative w-full max-w-md bg-white rounded-xl shadow-xl border border-slate-300 p-5">
            <div className="text-sm font-bold text-[#20243B]">Aviso</div>
            <div className="text-xs text-gray-600 mt-2 whitespace-pre-wrap">
              {alertState.message}
            </div>
            <div className="mt-5 flex justify-start gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => {
                  const r = alertState.resolve;
                  setAlertState({ open: false, message: "", resolve: null });
                  if (typeof r === "function") r(true);
                }}
                className="px-4 py-2 text-xs font-bold text-white bg-[#20243B] border border-[#20243B] shadow-md hover:bg-[#1a1e30] hover:shadow-lg rounded-lg transition-transform active:scale-95"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const ClientFinanceTab = ({ client }) => {
  const safeClient = client || null;
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="text-[11px] font-extrabold uppercase tracking-widest text-slate-500">
          Saúde Financeira
        </div>
        <div className="mt-3">
          <FinancialHealthWidget clientId={safeClient?.id} />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="text-[11px] font-extrabold uppercase tracking-widest text-slate-500">
          Gerar Cobrança
        </div>
        <div className="mt-3">
          <BillingGenerator client={safeClient} />
        </div>
      </div>
    </div>
  );
};

const ClientAutosProcessTab = ({ clientId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const id = String(clientId || "").trim();
    if (!id) {
      setItems([]);
      setError(null);
      setLoading(false);
      return;
    }

    let alive = true;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const out = await repo.fetchClientWorkspaces(appId, id);
        if (!alive) return;
        setItems(out);
      } catch (e) {
        if (!alive) return;
        setItems([]);
        setError(String(e?.message || e));
      } finally {
        if (alive) setLoading(false);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [clientId]);

  return (
    <div>
      {error ? (
        <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-xs text-red-700 whitespace-pre-wrap">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="mt-3 text-xs text-slate-500">Carregando...</div>
      ) : null}

      {!loading && !error && items.length === 0 ? (
        <div className="mt-3 text-xs text-slate-500">
          Nenhum caderno encontrado para este cliente.
        </div>
      ) : null}

      <div className="mt-4 grid grid-cols-1 gap-2">
        {items.map((w) => {
          const pieceType = String(w?.pieceType || "").trim();
          const clientName = String(w?.clientName || "").trim();
          const drive = String(w?.driveFolderId || "").trim();
          const title = pieceType || w?.title || "Caderno de Autos";
          const driveLink = drive
            ? `https://drive.google.com/drive/folders/${drive}`
            : "";

          return (
            <button
              key={w.id}
              type="button"
              onClick={() => {
                const wsId = String(w?.id || "").trim();
                if (!wsId) return;
                const url = `/file-hub?workspaceId=${encodeURIComponent(wsId)}`;
                window.open(url, "_blank", "noopener,noreferrer");
              }}
              className="text-left p-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div
                    className="text-sm font-extrabold text-slate-800 truncate"
                    title={String(title)}
                  >
                    {title}
                  </div>
                  {clientName ? (
                    <div className="text-xs text-slate-500 truncate">
                      Cliente: {clientName}
                    </div>
                  ) : null}
                  {drive ? (
                    <div className="text-[11px] text-slate-400 truncate">
                      Drive: {drive}
                    </div>
                  ) : null}
                </div>

                {driveLink ? (
                  <a
                    href={driveLink}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="px-3 py-2 rounded-lg text-xs font-extrabold uppercase tracking-widest border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  >
                    Drive
                  </a>
                ) : null}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const ClientAcervoTab = ({ clientId }) => {
  const [items, setItems] = useState([]);
  const [gedItems, setGedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [usedFallback, setUsedFallback] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [consentSaving, setConsentSaving] = useState(false);
  const [consentFeedback, setConsentFeedback] = useState("");

  const selectedConsentAction = useMemo(
    () => getConsentActionDescriptor(selectedDoc || {}),
    [selectedDoc],
  );
  const canEditSelectedDoc = useMemo(() => {
    return Boolean(
      resolvePublishedDocumentEditTarget(selectedDoc) &&
      String(selectedDoc?.documentId || selectedDoc?.id || "").trim(),
    );
  }, [selectedDoc]);

  const openFlyout = useCallback((row) => {
    setConsentFeedback("");
    setSelectedDoc(row);
    setFlyoutOpen(true);
  }, []);

  const closeFlyout = useCallback(() => {
    setConsentFeedback("");
    setFlyoutOpen(false);
  }, []);

  const handleEditSelectedDoc = useCallback(() => {
    const row = selectedDoc || {};
    const enrichedRow = {
      ...(row || {}),
      documentId: String(row?.documentId || row?.id || "").trim(),
      publicationSeriesId: String(
        row?.publicationSeriesId || row?.documentId || row?.id || "",
      ).trim(),
      publicationVersion: Math.max(
        0,
        Number(row?.publicationVersion || 0) || 0,
      ),
      title: String(row?.title || row?.fileName || row?.name || "").trim(),
      fileUrl: String(row?.fileUrl || "").trim(),
      url: String(row?.url || "").trim(),
      downloadUrl: String(row?.downloadUrl || "").trim(),
      mirror_url: String(row?.mirror_url || "").trim(),
      storagePath: String(row?.storagePath || "").trim(),
      storage_path: String(row?.storage_path || row?.storagePath || "").trim(),
    };
    const request = queuePublishedDocumentEdit(enrichedRow);
    if (!request) return;
    closeFlyout();
  }, [closeFlyout, selectedDoc]);

  const openDocumentViewer = useCallback((row) => {
    const title =
      String(row?.title || row?.fileName || row?.name || "Documento").trim() ||
      "Documento";
    const fileUrl = String(row?.fileUrl || "").trim();
    const documentId = String(row?.documentId || row?.id || "").trim();

    if (documentId || fileUrl) {
      const viewerUrl = `/view-published?${new URLSearchParams({
        ...(fileUrl ? { url: fileUrl } : {}),
        ...(documentId ? { documentId } : {}),
        ...(title ? { title } : {}),
      }).toString()}`;

      try {
        window.open(viewerUrl, "_blank", "noopener,noreferrer");
        return;
      } catch {}

      try {
        window.open(viewerUrl, "_blank");
        return;
      } catch {}
    }

    const externalUrl = String(row?.externalUrl || "").trim();
    if (!externalUrl) return;

    try {
      window.open(externalUrl, "_blank", "noopener,noreferrer");
    } catch {
      try {
        window.open(externalUrl, "_blank");
      } catch {}
    }
  }, []);

  const handleRequestConsent = useCallback(async () => {
    const documentId = String(
      selectedDoc?.documentId || selectedDoc?.id || "",
    ).trim();
    if (!documentId || consentSaving || selectedConsentAction.disabled) return;

    setConsentSaving(true);
    setConsentFeedback("");
    try {
      const result = await requestDocumentConsent({
        documentId,
        actorName: "Rodrigo Demeterco",
      });
      if (!result?.ok) {
        setConsentFeedback(
          String(
            result?.reason || "Não foi possível solicitar o consentimento.",
          ),
        );
        return;
      }

      const patch = {
        id: documentId,
        documentId,
        title: String(
          result?.data?.title ||
            result?.data?.name ||
            result?.data?.fileName ||
            selectedDoc?.title ||
            "",
        ).trim(),
        name: String(
          result?.data?.name ||
            result?.data?.title ||
            result?.data?.fileName ||
            selectedDoc?.title ||
            "",
        ).trim(),
        fileUrl: String(
          result?.data?.fileUrl ||
            result?.data?.url ||
            result?.data?.downloadUrl ||
            selectedDoc?.fileUrl ||
            "",
        ).trim(),
        requiresConsent: Boolean(result?.data?.requiresConsent),
        consentStatus: String(result?.data?.consentStatus || "").trim(),
        status: String(result?.data?.status || "").trim(),
        clientId: String(
          result?.data?.clientId ||
            result?.data?.context?.primaryEntityId ||
            clientId ||
            "",
        ).trim(),
      };

      setSelectedDoc((prev) => ({ ...(prev || {}), ...patch }));
      setGedItems((prev) =>
        Array.isArray(prev)
          ? prev.map((row) =>
              String(row?.id || "") === documentId ? { ...row, ...patch } : row,
            )
          : prev,
      );
      setConsentFeedback("Consentimento informado solicitado com sucesso.");
    } catch (e) {
      setConsentFeedback(
        String(e?.message || e || "Falha ao solicitar consentimento."),
      );
    } finally {
      setConsentSaving(false);
    }
  }, [clientId, consentSaving, selectedConsentAction.disabled, selectedDoc]);

  useEffect(() => {
    const id = String(clientId || "").trim();
    if (!id) {
      setItems([]);
      setGedItems([]);
      setError(null);
      setLoading(false);
      setUsedFallback(false);
      setSelectedDoc(null);
      setFlyoutOpen(false);
      return;
    }

    let alive = true;
    const run = async () => {
      setLoading(true);
      setError(null);
      setUsedFallback(false);

      try {
        const { items: out, usedFallback } = await repo.fetchClientGedLinks(id);
        if (usedFallback) setUsedFallback(true);

        if (!alive) return;
        setItems(out);

        try {
          const outGed = await repo.fetchClientDocuments(appId, id);
          if (alive) setGedItems(outGed);
        } catch (e) {
          try {
            const sample = await repo.fetchClientDocumentsFallback(appId, id);
            if (alive) setGedItems(sample);
          } catch {
            if (alive) setGedItems([]);
          }
        }
      } catch (e) {
        if (!alive) return;
        setItems([]);
        setGedItems([]);
        setError(String(e?.message || e));
      } finally {
        if (alive) setLoading(false);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [clientId]);

  return (
    <div>
      {error ? (
        <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-xs text-red-700 whitespace-pre-wrap">
          {error}
        </div>
      ) : null}

      {usedFallback ? (
        <div className="mt-3 text-[11px] text-amber-700">
          Resultados carregados via varredura (dados legados/sem padronização de
          vínculo).
        </div>
      ) : null}

      {loading ? (
        <div className="mt-3 text-xs text-slate-500">Carregando...</div>
      ) : null}

      {!loading &&
      !error &&
      items.length === 0 &&
      (Array.isArray(gedItems) ? gedItems : []).length === 0 ? (
        <div className="mt-3 text-xs text-slate-500">
          Nenhum documento vinculado para este cliente.
        </div>
      ) : null}

      <div className="mt-4 grid grid-cols-1 gap-2">
        {items.map((row) => {
          const fileName =
            String(row?.fileName || row?.name || "").trim() || "Documento";
          const driveFileId = String(row?.driveFileId || "").trim();
          const wsId = String(row?.workspaceId || "").trim();
          const driveLink = driveFileId
            ? `https://drive.google.com/file/d/${driveFileId}/view`
            : "";

          const addedAtTxt = (() => {
            try {
              const dt = row?.addedAt?.toDate ? row.addedAt.toDate() : null;
              if (!dt) return "";
              return dt.toLocaleString();
            } catch {
              return "";
            }
          })();

          const flyoutRow = {
            id: String(row?.id || driveFileId || fileName),
            documentId: "",
            title: fileName,
            fileUrl: "",
            externalUrl: driveLink,
            workspaceId: wsId,
            requiresConsent: false,
            consentStatus: "",
            status: "",
          };

          return (
            <div
              key={String(row?.id || driveFileId || fileName)}
              className="p-3 rounded-xl border border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => openFlyout(flyoutRow)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openFlyout(flyoutRow);
                }
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div
                    className="text-sm font-extrabold text-slate-800 truncate"
                    title={fileName}
                  >
                    {fileName}
                  </div>
                  {wsId ? (
                    <div className="text-[11px] text-slate-500 truncate">
                      Workspace: {wsId}
                    </div>
                  ) : null}
                  {addedAtTxt ? (
                    <div className="text-[11px] text-slate-400 truncate">
                      Adicionado: {addedAtTxt}
                    </div>
                  ) : null}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {wsId ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const url = `/file-hub?workspaceId=${encodeURIComponent(wsId)}`;
                        window.open(url, "_blank", "noopener,noreferrer");
                      }}
                      className="px-3 py-2 rounded-lg text-xs font-extrabold uppercase tracking-widest border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                    >
                      Hub
                    </button>
                  ) : null}

                  {driveLink ? (
                    <a
                      href={driveLink}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="px-3 py-2 rounded-lg text-xs font-extrabold uppercase tracking-widest border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                    >
                      Abrir
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {!loading &&
      !error &&
      (Array.isArray(gedItems) ? gedItems : []).length > 0 ? (
        <div className="mt-6">
          <div className="text-[11px] font-extrabold uppercase tracking-widest text-slate-600">
            Uploads (GED Global)
          </div>
          <div className="mt-2 grid grid-cols-1 gap-2">
            {(Array.isArray(gedItems) ? gedItems : []).map((row) => {
              const fileName =
                String(row?.name || row?.title || "").trim() || "Documento";
              const createdTxt = (() => {
                try {
                  const dt = row?.createdAt?.toDate
                    ? row.createdAt.toDate()
                    : null;
                  if (!dt) return "";
                  return dt.toLocaleString();
                } catch {
                  return "";
                }
              })();
              const owner = String(
                row?.context?.primaryEntityName || row?.linkedClient || "",
              ).trim();
              const cat = String(
                row?.classification?.category || row?.category || "",
              ).trim();
              const status = String(row?.status || "").trim();

              const flyoutRow = {
                ...row,
                documentId: String(row?.id || "").trim(),
                title: fileName,
                fileUrl: String(
                  row?.fileUrl ||
                    row?.url ||
                    row?.downloadUrl ||
                    row?.webViewLink ||
                    "",
                ).trim(),
                clientId: String(
                  row?.clientId ||
                    row?.context?.primaryEntityId ||
                    clientId ||
                    "",
                ).trim(),
              };

              return (
                <div
                  key={String(row?.id || fileName)}
                  className="p-3 rounded-xl border border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onClick={() => openFlyout(flyoutRow)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openFlyout(flyoutRow);
                    }
                  }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div
                        className="text-sm font-extrabold text-slate-800 truncate"
                        title={fileName}
                      >
                        {fileName}
                      </div>
                      {owner ? (
                        <div className="text-[11px] text-slate-500 truncate">
                          Vínculo: {owner}
                        </div>
                      ) : null}
                      {cat || status || createdTxt ? (
                        <div className="text-[11px] text-slate-400 truncate">
                          {[
                            cat ? `Categoria: ${cat}` : "",
                            status ? `Status: ${status}` : "",
                            createdTxt ? `Criado: ${createdTxt}` : "",
                          ]
                            .filter(Boolean)
                            .join(" • ")}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {flyoutOpen && selectedDoc ? (
        <div className="fixed inset-0 z-[320]">
          <div className="absolute inset-0 bg-black/20" onClick={closeFlyout} />
          <div className="absolute top-0 right-0 h-full w-full max-w-[420px] bg-white border-l border-slate-200 shadow-2xl flex flex-col">
            <div className="px-4 py-4 border-b border-slate-200 bg-slate-50">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-xs font-extrabold uppercase tracking-widest text-slate-500">
                    Documento
                  </div>
                  <div
                    className="mt-1 text-sm font-extrabold text-[#20243B] truncate"
                    title={selectedDoc?.title || ""}
                  >
                    {selectedDoc?.title || "Documento"}
                  </div>
                  {String(selectedDoc?.documentId || "").trim() ? (
                    <div className="mt-1 text-[11px] text-slate-500 truncate">
                      ID: {selectedDoc.documentId}
                    </div>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={closeFlyout}
                  className="px-3 py-2 rounded-lg text-xs font-extrabold uppercase tracking-widest border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                >
                  Fechar
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-2">
                <div className="text-[11px] font-extrabold uppercase tracking-widest text-slate-500">
                  Status do Consentimento
                </div>
                <div className="text-sm font-semibold text-slate-700">
                  {selectedConsentAction.label}
                </div>
              </div>

              {consentFeedback ? (
                <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs font-semibold text-slate-600 whitespace-pre-wrap">
                  {consentFeedback}
                </div>
              ) : null}
            </div>

            <div className="border-t border-slate-200 bg-white p-4">
              <div className="flex flex-col gap-2 w-full">
                <button
                  type="button"
                  onClick={() => openDocumentViewer(selectedDoc)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                >
                  Abrir Documento
                </button>
                {String(selectedDoc?.documentId || "").trim() ? (
                  <button
                    type="button"
                    onClick={handleRequestConsent}
                    disabled={consentSaving || selectedConsentAction.disabled}
                    className={
                      "w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold border " +
                      (consentSaving
                        ? "border-amber-200 bg-amber-100 text-amber-800 cursor-wait"
                        : selectedConsentAction.className)
                    }
                  >
                    {consentSaving
                      ? "Solicitando..."
                      : selectedConsentAction.label}
                  </button>
                ) : null}
                {canEditSelectedDoc ? (
                  <button
                    type="button"
                    onClick={handleEditSelectedDoc}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold border border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100"
                  >
                    ✏️ Editar Documento
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export const ClientManager = () => {
  const [dependentPicker, setDependentPicker] = useState({
    open: false,
    currentClient: null,
  });

  const [peekOpen, setPeekOpen] = useState(false);
  const [peekClient, setPeekClient] = useState(undefined);
  const [peekTab, setPeekTab] = useState("CADASTRO");
  const [peekDirty, setPeekDirty] = useState(false);

  const isDev = Boolean(
    typeof import.meta !== "undefined" &&
    import.meta?.env &&
    import.meta.env.DEV,
  );

  const [peekConfirmState, setPeekConfirmState] = useState({
    open: false,
    message: "",
    resolve: null,
  });

  const safePeekConfirm = useCallback((message) => {
    return new Promise((resolve) => {
      setPeekConfirmState({
        open: true,
        message: String(message || ""),
        resolve,
      });
    });
  }, []);

  const [refreshSignal, setRefreshSignal] = useState(0);

  const openPeek = useCallback((client) => {
    setPeekClient(client || null);
    setPeekTab("CADASTRO");
    setPeekDirty(false);
    setPeekOpen(true);
  }, []);

  useEffect(() => {
    if (!isDev) return;
    if (typeof window === "undefined") return;

    if (!window.__RDA_CHAOS__) window.__RDA_CHAOS__ = {};

    const open = () => {
      openPeek(null);
    };

    const close = () => {
      if (!peekOpen) return;
      if (peekDirty) return;
      setPeekOpen(false);
      setPeekClient(undefined);
    };

    const toggle = () => {
      if (peekOpen) close();
      else open();
    };

    const api = { open, close, toggle };
    window.__RDA_CHAOS__.clientPeek = api;

    const onChaosEvent = (e) => {
      const action = String(e?.detail?.action || "").toLowerCase();
      if (action === "open") open();
      else if (action === "close") close();
      else toggle();
    };

    window.addEventListener("rda:chaos:clientPeek", onChaosEvent);

    return () => {
      window.removeEventListener("rda:chaos:clientPeek", onChaosEvent);
      try {
        if (window.__RDA_CHAOS__ && window.__RDA_CHAOS__.clientPeek === api) {
          delete window.__RDA_CHAOS__.clientPeek;
        }
      } catch {}
    };
  }, [isDev, openPeek, peekDirty, peekOpen]);

  const handleCloseRequest = useCallback(async () => {
    if (peekDirty) {
      const ok = await safePeekConfirm(
        "Existem alterações não salvas. Deseja fechar e perder os dados?",
      );
      if (!ok) return;
    }
    setPeekOpen(false);
    setPeekClient(undefined);
  }, [peekDirty, safePeekConfirm]);

  const handleOpenClient = useCallback(
    async (client) => {
      if (!client) return;
      if (peekOpen && peekDirty) {
        const ok = await safePeekConfirm(
          "Existem alterações não salvas. Deseja abrir outro cliente e perder os dados?",
        );
        if (!ok) return;
      }
      openPeek(client);
    },
    [peekOpen, peekDirty, openPeek, safePeekConfirm],
  );

  const handleDuplicateClient = useCallback(
    async (client) => {
      if (!client) return;
      if (peekOpen && peekDirty) {
        const ok = await safePeekConfirm(
          "Existem alterações não salvas. Deseja duplicar outro cliente e perder os dados?",
        );
        if (!ok) return;
      }
      const clone = JSON.parse(JSON.stringify(client));
      delete clone.id;
      delete clone.createdAt;
      delete clone.updatedAt;
      if (clone.qualification?.physical) {
        clone.qualification.physical.cpf = "";
        clone.qualification.physical.rg = "";
        clone.qualification.physical.orgaoEmissor = "";
      }
      if (clone.qualification?.minor) {
        clone.qualification.minor.cpf = "";
        clone.qualification.minor.rg = "";
      }
      if (clone.qualification?.legal) {
        clone.qualification.legal.cnpj = "";
      }
      if (clone.qualification?.animal) {
        clone.qualification.animal.microchip = "";
        clone.qualification.animal.rga = "";
        clone.qualification.animal.chipId = "";
      }
      if (clone.qualification?.imprisoned) {
        clone.qualification.imprisoned.cpf = "";
        clone.qualification.imprisoned.rg = "";
        clone.qualification.imprisoned.matriculaPrisional = "";
        clone.qualification.imprisoned.rji = "";
      }
      if (clone.contact) {
        clone.contact.email = "";
      }
      clone.onboardingStatus = "PENDING_APPROVAL";
      clone.isDeleted = false;
      clone.auditLog = [];
      openPeek(clone);
    },
    [peekOpen, peekDirty, openPeek, safePeekConfirm],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const onClientPrefill = async (e) => {
      const payload = normalizeDocumentExtractedData(e?.detail?.prefillData);
      if (!payload || typeof payload !== "object") return;

      if (peekOpen && peekDirty) {
        const ok = await safePeekConfirm(
          "Existem alterações não salvas. Deseja abrir o pré-cadastro da triagem e perder os dados atuais?",
        );
        if (!ok) return;
      }

      openPeek({
        type: CLIENT_TYPES.PHYSICAL,
        qualification: {
          physical: {
            name: String(payload?.name || "").trim(),
            cpf: String(payload?.cpf || "").trim(),
            birthDate: String(payload?.birthDate || "").trim(),
            motherName: String(payload?.motherName || "").trim(),
            fatherName: String(payload?.fatherName || "").trim(),
          },
        },
      });
    };

    window.addEventListener("rda:client-prefill", onClientPrefill);
    return () =>
      window.removeEventListener("rda:client-prefill", onClientPrefill);
  }, [openPeek, peekDirty, peekOpen, safePeekConfirm]);

  const createDependentFromClient = (currentClient, kind) => {
    if (!currentClient?.id) return;

    const tutorName =
      currentClient?.qualification?.physical?.name ||
      currentClient?.qualification?.legal?.companyName ||
      currentClient?.qualificationText ||
      "Responsável";
    const tutorCpf = currentClient?.qualification?.physical?.cpf || "";
    const base = JSON.parse(JSON.stringify(ClientSchema));

    if (kind === "animal") {
      base.type = CLIENT_TYPES.ANIMAL;
      base.anchor = {
        ...(base.anchor || {}),
        id: String(currentClient.id),
        name: tutorName,
        cpf: tutorCpf,
        role: ANCHOR_ROLES.TUTOR,
      };
      base.__tutorLocked = true;
      openPeek(normalizeRepresentativeLink(base, currentClient));
      return;
    } else {
      base.type = CLIENT_TYPES.MINOR;
      base.qualification.minor.representative = {
        ...(base.qualification.minor.representative || {}),
        name: tutorName,
        id: String(currentClient.id),
      };
      base.anchor = {
        ...(base.anchor || {}),
        id: String(currentClient.id),
        name: tutorName,
        cpf: tutorCpf,
        role: ANCHOR_ROLES.PARENT,
      };
      base.__tutorLocked = true;
    }

    openPeek(base);
  };

  const handleCreateDependent = (currentClient) => {
    if (!currentClient?.id) return;
    setDependentPicker({ open: true, currentClient });
  };

  const handleSave = async (clientData, opts = {}) => {
    const keepOpen = Boolean(opts?.keepOpen);
    try {
      const type = clientData?.type;
      const cpfFmt = clientData?.qualification?.physical?.cpf || "";
      const cnpjFmt = clientData?.qualification?.legal?.cnpj || "";
      const phoneFmt = clientData?.contact?.phonePrimary || "";

      if (
        type === SAFE_CLIENT_TYPES.PHYSICAL &&
        cpfFmt &&
        String(cpfFmt).length < 14
      ) {
        console.log("[CRM-SAVE-DEBUG] Early return #1: CPF incompleto", {
          cpfFmt,
          length: String(cpfFmt).length,
        });
        alert("CPF incompleto");
        return;
      }
      if (
        type === SAFE_CLIENT_TYPES.LEGAL &&
        cnpjFmt &&
        String(cnpjFmt).length < 18
      ) {
        console.log("[CRM-SAVE-DEBUG] Early return #2: CNPJ incompleto", {
          cnpjFmt,
          length: String(cnpjFmt).length,
        });
        alert("CNPJ incompleto");
        return;
      }
      if (phoneFmt && String(phoneFmt).length < 14) {
        console.log("[CRM-SAVE-DEBUG] Early return #3: Telefone inválido", {
          phoneFmt,
          length: String(phoneFmt).length,
        });
        alert("Telefone inválido");
        return;
      }

      const isPhysical = clientData?.type === SAFE_CLIENT_TYPES.PHYSICAL;
      const clientAge = isPhysical
        ? calcAge(clientData?.qualification?.physical?.birthDate)
        : null;
      const requiresAnchor =
        !isPhysical || (typeof clientAge === "number" && clientAge < 18);
      if (requiresAnchor && !clientData?.anchor?.id && !clientData?.id) {
        console.log(
          "[CRM-SAVE-DEBUG] Early return #4: requiresAnchor bloqueou (novo cadastro)",
          {
            requiresAnchor,
            anchorId: clientData?.anchor?.id,
            clientId: clientData?.id,
            type: clientData?.type,
          },
        );
        alert("BLOQUEIO: Este cadastro exige um Responsável (Âncora) com CPF.");
        return;
      }

      const cpfToCheck = clientData?.qualification?.physical?.cpf;
      if (cpfToCheck) {
        const conflitoCpf = await repo.checkCpfConflict(
          appId,
          cpfToCheck,
          clientData.id,
        );
        if (conflitoCpf) {
          console.log("[CRM-SAVE-DEBUG] Early return #5: CPF conflitante", {
            cpfToCheck,
            clientId: clientData?.id,
          });
          const nomeConflito =
            conflitoCpf.data()?.qualification?.physical?.name ||
            conflitoCpf.data()?.qualification?.minor?.name ||
            "Cliente existente";
          alert(
            `OPERAÇÃO BLOQUEADA: Este CPF já está cadastrado no sistema para o cliente "${nomeConflito}".`,
          );
          return;
        }
      }
      const cnpjToCheck = clientData?.qualification?.legal?.cnpj;
      if (cnpjToCheck) {
        const conflitoCnpj = await repo.checkCnpjConflict(
          appId,
          cnpjToCheck,
          clientData.id,
        );
        if (conflitoCnpj) {
          console.log("[CRM-SAVE-DEBUG] Early return #6: CNPJ conflitante", {
            cnpjToCheck,
            clientId: clientData?.id,
          });
          const nomeConflito =
            conflitoCnpj.data()?.qualification?.legal?.companyName ||
            "Empresa existente";
          alert(
            `OPERAÇÃO BLOQUEADA: Este CNPJ já está cadastrado no sistema para o cliente "${nomeConflito}".`,
          );
          return;
        }
      }

      const toTitleCase = (raw) => {
        const s = String(raw || "").trim();
        if (!s) return "";
        return s
          .toLowerCase()
          .split(/\s+/g)
          .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ""))
          .join(" ");
      };

      // MC101 (11/09/2026): decisão do titular no artefato "Anatomia da
      // Maiúscula" — o cadastro PARA de forçar maiúscula nos campos de
      // nome de pessoa/PJ ao salvar (Decisão 3). Antes, `.toUpperCase()`
      // aqui já era inconsistente por campo (mãe/pai/representante nunca
      // eram forçados, só uma lista arbitrária de outros campos era) — a
      // fonte de verdade agora é "como foi digitado" (trim só), e quem
      // decide como EXIBIR (Nome Próprio, ex.: `toTitleCasePt` em
      // `formatters.js`) faz isso na hora de mostrar, não na hora de
      // salvar. O texto de qualificação jurídica (`generateQualificationText`
      // abaixo) não é afetado por esta mudança — `ClientSchema.js` aplica
      // a própria maiúscula (`strongUpper`) ao montar o documento,
      // independente de como o nome chegou aqui (Decisão 1: mantém
      // maiúsculo só no documento formal).
      const sanitizedClient = (() => {
        const next = JSON.parse(JSON.stringify(clientData || {}));

        const normalizedRepresentativeClient =
          normalizeRepresentativeLink(next);
        if (normalizedRepresentativeClient) {
          Object.assign(next, normalizedRepresentativeClient);
        }

        if (next?.qualification?.physical) {
          next.qualification.physical.name = next.qualification.physical.name
            ? String(next.qualification.physical.name).trim()
            : next.qualification.physical.name;
          next.qualification.physical.socialName = next.qualification.physical
            .socialName
            ? String(next.qualification.physical.socialName).trim()
            : next.qualification.physical.socialName;
          next.qualification.physical.profession = toTitleCase(
            next.qualification.physical.profession,
          );
          next.qualification.physical.spouseName = next.qualification.physical
            .spouseName
            ? String(next.qualification.physical.spouseName).trim()
            : next.qualification.physical.spouseName;
        }

        if (next?.qualification?.legal) {
          next.qualification.legal.companyName = next.qualification.legal
            .companyName
            ? String(next.qualification.legal.companyName).trim()
            : next.qualification.legal.companyName;
          next.qualification.legal.tradeName = next.qualification.legal
            .tradeName
            ? String(next.qualification.legal.tradeName).trim()
            : next.qualification.legal.tradeName;
        }

        if (next?.qualification?.minor) {
          next.qualification.minor.name = next.qualification.minor.name
            ? String(next.qualification.minor.name).trim()
            : next.qualification.minor.name;
        }

        if (next?.qualification?.animal) {
          next.qualification.animal.name = next.qualification.animal.name
            ? String(next.qualification.animal.name).trim()
            : next.qualification.animal.name;
          next.qualification.animal.scientificName = String(
            next.qualification.animal.scientificName || "",
          ).trim();
          next.qualification.animal.species = toTitleCase(
            next.qualification.animal.species,
          );
          next.qualification.animal.breed = toTitleCase(
            next.qualification.animal.breed,
          );
          next.qualification.animal.coat = {
            ...(next.qualification.animal.coat || {}),
            color: toTitleCase(next?.qualification?.animal?.coat?.color),
            pattern: String(
              next?.qualification?.animal?.coat?.pattern || "",
            ).trim(),
            texture: String(
              next?.qualification?.animal?.coat?.texture || "",
            ).trim(),
          };
          next.qualification.animal.distinctiveMarks = Array.isArray(
            next?.qualification?.animal?.distinctiveMarks,
          )
            ? next.qualification.animal.distinctiveMarks
                .map((item) => String(item || "").trim())
                .filter(Boolean)
            : [];
          next.qualification.animal.registries = Array.isArray(
            next?.qualification?.animal?.registries,
          )
            ? next.qualification.animal.registries
                .map((item) => String(item || "").trim())
                .filter(Boolean)
            : [];
          const normalizedRepresentative = normalizeRepresentativeLink(next);
          if (normalizedRepresentative) {
            next.anchor = normalizedRepresentative.anchor;
            next.qualification = normalizedRepresentative.qualification;
          }
        }

        if (next?.qualification?.imprisoned) {
          next.qualification.imprisoned.name = next.qualification.imprisoned
            .name
            ? String(next.qualification.imprisoned.name).trim()
            : next.qualification.imprisoned.name;
          next.qualification.imprisoned.prisonUnit = toTitleCase(
            next.qualification.imprisoned.prisonUnit,
          );
        }

        if (next?.qualification?.estate) {
          next.qualification.estate.deceasedName = next.qualification.estate
            .deceasedName
            ? String(next.qualification.estate.deceasedName).trim()
            : next.qualification.estate.deceasedName;
        }

        if (next?.qualification?.condominium) {
          next.qualification.condominium.name = next.qualification.condominium
            .name
            ? String(next.qualification.condominium.name).trim()
            : next.qualification.condominium.name;
        }

        if (next?.qualification?.bankruptcy) {
          next.qualification.bankruptcy.companyName = next.qualification
            .bankruptcy.companyName
            ? String(next.qualification.bankruptcy.companyName).trim()
            : next.qualification.bankruptcy.companyName;
        }

        return next;
      })();

      const searchKeywords = SearchEngine.createClientIndex(sanitizedClient);
      // FIX ID-FANTASMA (21/08/2026): "id" NUNCA deve ser gravado como campo
      // dentro do próprio documento — o ID já vive no path do Firestore. Sem
      // este destructure, sanitizedClient.id (às vezes null, em cliente novo
      // ainda não salvo) ia dentro do payload e ficava congelado no documento.
      // O leitor de conflito de interesses (ConflictRepository.fetchClientsBy*)
      // faz `{ id: d.id, ...dados }` — se "dados" já tem um campo "id", ele
      // SOBRESCREVE o ID real do Firestore, quebrando tanto a exclusão do
      // próprio registro na varredura de duplicidade quanto a mensagem
      // "H3 — Duplicidade Absoluta ... (ID: )" (ficava em branco). Caso
      // confirmado: cliente real com campo id:null gravado
      // na criação em 17/08/2026.
      const { id: _sanitizedClientId, ...sanitizedClientWithoutId } = sanitizedClient || {};
      const payload = {
        ...sanitizedClientWithoutId,
        updatedAt: repo.serverTimestamp(),
        qualificationText: generateQualificationText(sanitizedClient),
        searchKeywords,
      };

      if (clientData.id) await repo.updateClient(appId, clientData.id, payload);
      else await repo.createClient(appId, payload);

      // 🆕 PUBLISH: Notificar todos os módulos dependentes que o cliente foi atualizado
      try {
        const clientId = String(payload?.id || clientData?.id || "").trim();
        const clientName = String(
          payload?.qualification?.physical?.name ||
            payload?.qualification?.legal?.companyName ||
            clientData?.qualification?.physical?.name ||
            clientData?.qualification?.legal?.companyName ||
            "",
        ).trim();
        const qualificationText = String(
          payload?.qualificationText || clientData?.qualificationText || "",
        ).trim();

        console.log("[PUBLISH] Emitindo rda:client-updated:", {
          clientId,
          clientName,
          qualificationText: qualificationText.slice(0, 80),
        });
        window.dispatchEvent(
          new CustomEvent("rda:client-updated", {
            detail: {
              clientId,
              clientName,
              qualificationText,
              cpf: String(
                payload?.qualification?.physical?.cpf ||
                  clientData?.qualification?.physical?.cpf ||
                  "",
              ).trim(),
              email: String(
                payload?.contact?.email || clientData?.contact?.email || "",
              ).trim(),
              timestamp: Date.now(),
            },
          }),
        );
      } catch (e) {
        console.warn("[PUBLISH] Falha ao emitir evento rda:client-updated:", e);
      }

      setRefreshSignal((v) => v + 1);
      setPeekDirty(false);
      if (!keepOpen) {
        setPeekOpen(false);
        setPeekClient(undefined);
      } else {
        // Salvou e continua aberto — atualiza o peekClient com os dados
        // que acabaram de ir pro Firestore, sem fechar o painel. Usa
        // `clientData` (o parâmetro original, com `id` garantido — vem de
        // `formData` no ClientForm) e não `payload` (construído mais acima
        // sem `id`, específico da escrita no Firestore).
        setPeekClient((prev) => ({ ...(prev || {}), ...clientData }));
      }
    } catch (e) {
      alert("Erro ao salvar no banco: " + e.message);
      console.error(e);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {dependentPicker.open ? (
        <div className="fixed inset-0 z-[600] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() =>
              setDependentPicker({ open: false, currentClient: null })
            }
          />
          <div className="relative w-full max-w-md bg-white rounded-xl shadow-xl border border-slate-300 p-5">
            <div className="text-sm font-bold text-[#20243B]">
              Novo representado
            </div>
            <div className="text-xs text-gray-600 mt-2">
              Selecione o tipo do representado a ser cadastrado.
            </div>
            <div className="mt-5 flex justify-start gap-2 flex-wrap">
              <button
                type="button"
                onClick={() =>
                  setDependentPicker({ open: false, currentClient: null })
                }
                className="px-4 py-2 text-xs font-bold text-gray-700 bg-white border border-gray-300 shadow-sm hover:bg-gray-50 hover:shadow-md rounded-lg transition-transform active:scale-95"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  const c = dependentPicker.currentClient;
                  setDependentPicker({ open: false, currentClient: null });
                  createDependentFromClient(c, "minor");
                }}
                className="px-4 py-2 text-xs font-bold text-white bg-[#20243B] border border-[#20243B] shadow-md hover:bg-[#1a1e30] rounded-lg transition-transform active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Menor/Incapaz
              </button>
              <button
                type="button"
                onClick={() => {
                  const c = dependentPicker.currentClient;
                  setDependentPicker({ open: false, currentClient: null });
                  createDependentFromClient(c, "animal");
                }}
                className="px-4 py-2 text-xs font-bold text-white bg-[#20243B] border border-[#20243B] shadow-md hover:bg-[#1a1e30] hover:shadow-lg rounded-lg transition-transform active:scale-95"
              >
                Animal
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {peekConfirmState.open ? (
        <div className="fixed inset-0 z-[650] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              const r = peekConfirmState.resolve;
              setPeekConfirmState({ open: false, message: "", resolve: null });
              if (typeof r === "function") r(false);
            }}
          />
          <div className="relative w-full max-w-md bg-white rounded-xl shadow-xl border border-slate-300 p-5">
            <div className="text-sm font-bold text-[#20243B]">Confirmar</div>
            <div className="text-xs text-gray-600 mt-2 whitespace-pre-wrap">
              {peekConfirmState.message}
            </div>
            <div className="mt-5 flex justify-start gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => {
                  const r = peekConfirmState.resolve;
                  setPeekConfirmState({
                    open: false,
                    message: "",
                    resolve: null,
                  });
                  if (typeof r === "function") r(false);
                }}
                className="px-4 py-2 text-xs font-bold text-gray-700 bg-white border border-gray-300 shadow-sm hover:bg-gray-50 hover:shadow-md rounded-lg transition-transform active:scale-95"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  const r = peekConfirmState.resolve;
                  setPeekConfirmState({
                    open: false,
                    message: "",
                    resolve: null,
                  });
                  if (typeof r === "function") r(true);
                }}
                className="px-4 py-2 text-xs font-bold text-white bg-[#20243B] border border-[#20243B] shadow-md hover:bg-[#1a1e30] hover:shadow-lg rounded-lg transition-transform active:scale-95"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <ClientList
        refreshSignal={refreshSignal}
        onCreate={() => openPeek(null)}
        onEdit={(c) => {
          handleOpenClient(c);
        }}
        onDuplicate={handleDuplicateClient}
      />

      {peekOpen && peekClient !== undefined ? (
        <ClientSidePeek
          isOpen={peekOpen}
          client={peekClient}
          activeTab={peekTab}
          setActiveTab={setPeekTab}
          onCloseRequest={handleCloseRequest}
        >
          {peekTab === "CADASTRO" ? (
            <ClientForm
              key={String(peekClient?.id || "new")}
              initialData={peekClient}
              onSave={handleSave}
              onCancel={handleCloseRequest}
              onOpenClient={handleOpenClient}
              onCreateDependent={handleCreateDependent}
              onDirtyChange={setPeekDirty}
            />
          ) : peekTab === "PRONTUARIO" ? (
            <ClientNotesTab clientId={peekClient?.id} />
          ) : peekTab === "AUTOS" ? (
            <ClientAutosProcessTab clientId={peekClient?.id} />
          ) : peekTab === "ACERVO" ? (
            <ClientAcervoTab clientId={peekClient?.id} />
          ) : peekTab === "FINANCEIRO" ? (
            <ClientFinanceTab client={peekClient} />
          ) : (
            <div className="text-xs text-slate-500">Em breve.</div>
          )}
        </ClientSidePeek>
      ) : null}
    </div>
  );
};
export { ClientForm };
